import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useMediaQuery } from "@/lib/hooks";
import { useSearchParams, Link } from "react-router-dom";
import { squircleStyle, linkGradient } from "@/utils/tileStyle";
import { AI_PHOTO_ULTRA_HD } from "@/constants/aiPhotoQuality";
import { PHOTO_PRINT_DEFAULTS, photoPrintSizeOf } from "@/constants/photoPrintSizes";
import { VISA_GROUPS } from "@/pages/user/generate-image/VisaCountryModal";
import {
  IconCheck,
  IconCalendar,
  IconFileDescription,
  IconCrown,
  IconUsers,
  IconPhoto,
  IconCreditCard,
  IconCoins,
  IconCurrencyTaka,
  IconSparkles,
  IconSettings,
  IconShoppingCart,
  IconReceipt,
  IconReceipt2,
  IconArrowRight,
  IconInfoCircle,
  IconRefresh,
  IconPencil,
  IconLinkOff,
  IconAlertCircle,
  IconX,
  IconPlus,
  IconTrash,
  IconKeyboard,
  IconPrinter,
  IconForms,
  IconPuzzle,
  IconUserCircle,
  IconBuildingStore,
  IconTools,
  IconLink,
  IconFileInvoice,
  IconUserPlus,
  IconDownload
} from "@tabler/icons-react";
import { Switch as UiSwitch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { notifications } from "@/lib/notifications";
import { useAuthStore } from "../../../store/authStore";
import { useOfficeSpaceStore } from "../../../store/officeSpaceStore";
import PhotoCollectorSettings from "@/components/common/PhotoCollectorSettings";
import SaveButton from "@/components/common/SaveButton";
import InvoiceSettings from "@/components/invoice/InvoiceSettings";
import BiggaponModal from "@/components/common/BiggaponModal";
import { useIncomeExpenseStore } from "../../../store/incomeExpenseStore";
import authApi from "../../../services/authApi";
import usageService from "../../../services/usageService";
import { toBnDigits } from "../../../utils/bnFormat";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import { DEFAULT_PHOTO_NAME_SETTINGS, buildPhotoPrefix, shopInitials } from "../../../utils/photoFileName";
import PaginationBar from "../../../components/common/PaginationBar";
import BuyCreditTab from "../profile/BuyCreditTab";
// Migration nudge disabled — import kept commented for easy re-enable.
// import CreditMigrationBanner from "../../../components/common/CreditMigrationBanner";
import LowCreditBanner from "../../../components/common/LowCreditBanner";
import { creditParts } from "../../../utils/credit";
import PaymentHistoryTable from "../profile/PaymentHistoryTable";
import paymentService from "../../../services/paymentService";
import { CREDIT_COSTS, WALLET_CREDIT_COSTS } from "../../../config/creditPackages";
import { MOBILE_APP_URL } from "../../../config/appLinks";
import { ALL_DRESS_COLORS } from "../../../constants/dressColors";
import { buildSuggestionsForType, buildShopServiceList, INCOME_MODULES, COMMON_INCOME_SERVICES } from "../../../lib/incomeExpenseDefaults";
import { sameData } from "../../../utils/sameData";
import { isDressStyleEnabled, toggleDressStyle } from "../../../utils/dressStyles";
import { DEFAULT_PROMPT_FLAGS, photoEditSame, plainSuggestion } from "./settingsDirty";
import { PHOTO_TOOL_ICONS } from "../../../constants/photoToolIcons";
import RateChartButton from "../../../components/common/RateChartButton";
import { sanitizeText, hasUnsafeContent } from "../../../utils/sanitize";
import { DressPreviewSvg } from "../../../components/common/DressPreviewSvg";
import InvoiceTemplatePicker from "../../../components/invoice/InvoiceTemplatePicker";

// ── Active payment gateway ──────────────────────────────────────────────────
// Which buy-credit tab an OWNER sees (Moneybag vs SSLCommerz) is driven by the
// global "activePaymentGateway" Setting, exposed via GET /api/credits/config as
// creditConfig.activePaymentGateway ('moneybag' | 'sslCommerz'). Exactly one payment
// tab shows per OWNER. Fallback when unset/loading is 'sslCommerz'.
const DEFAULT_PAYMENT_GATEWAY = "sslCommerz";

// ── Shared Tailwind class strings ──────────────────────────────────────────
const inputCls =
  "w-full px-3 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 disabled:opacity-50 disabled:cursor-not-allowed";

// Markup-spec toggle size: 46×26px track with a 20px knob (border-[3px] gives the
// 3px inset + 20px travel so the knob lands flush right when on).
const switchCls = "h-[26px] w-[46px] border-[3px]";

// Renders a module SVG (public/module-icons) as a single-colour glyph tinted to
// the current text colour via CSS mask, so credit-cost cards reuse the exact
// module art shown in the header navigation.
const ModuleGlyph = ({ src, size = 18 }) => (
  <span
    aria-hidden
    className="inline-block bg-current shrink-0"
    style={{
      width: size,
      height: size,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
    }}
  />
);

// Translation key for a credit transaction's module badge. Question-paper rows
// all carry module QUESTION_PAPER, so we split them by metadata.action into
// distinct "create" / "export" labels; everything else maps 1:1 to its module.
function creditModuleKey(tx) {
  if (tx.module === "QUESTION_PAPER") {
    if (tx.metadata?.action === "create") return "profile.creditModule_QUESTION_CREATE";
    if (tx.metadata?.action === "export") return "profile.creditModule_QUESTION_EXPORT";
  }
  return `profile.creditModule_${tx.module}`;
}

// ── Small reusable primitives ──────────────────────────────────────────────

// Thin wrapper around the shadcn Switch that preserves the existing
// `checked`/`onChange(event)` API used throughout this page.
function Switch({ checked, onChange, label, icon }) {
  const handle = (val) => onChange?.({ currentTarget: { checked: val }, target: { checked: val } });
  return (
    <div className={`inline-flex gap-2 select-none ${icon ? "items-center" : "items-start"}`}>
      <UiSwitch checked={!!checked} onCheckedChange={handle} className={`${icon ? "" : "mt-0.5 "}shrink-0 ${switchCls}`} />
      {/* Optional tool icon (black/grey line art → inverted on dark surfaces); clicking it toggles like the label. */}
      {icon && (
        <img
          src={icon} alt="" loading="lazy" draggable="false"
          onClick={() => handle(!checked)}
          className="h-9 w-9 shrink-0 cursor-pointer object-contain dark:invert dark:brightness-110"
        />
      )}
      {label && (
        <span
          onClick={() => handle(!checked)}
          className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug cursor-pointer"
        >
          {label}
        </span>
      )}
    </div>
  );
}

// Markup-style settings row: title + description on the left, toggle on the right.
function ToggleCard({ title, description, checked, onCheckedChange, children }) {
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm px-4 sm:px-[22px] py-[18px] flex items-center gap-4 sm:gap-[18px]">
      <div className="flex-1 min-w-0">
        <div className="text-[15.5px] font-bold text-zinc-800 dark:text-zinc-100">{title}</div>
        <div className="text-[13px] text-zinc-500 dark:text-zinc-400 mt-[3px] leading-relaxed">{description}</div>
        {children}
      </div>
      <UiSwitch checked={!!checked} onCheckedChange={onCheckedChange} className={`shrink-0 ${switchCls}`} />
    </div>
  );
}

function Tip({ label, children }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-zinc-800 text-white text-xs whitespace-nowrap z-50 pointer-events-none">
          {label}
        </div>
      )}
    </div>
  );
}

function Badge({ children, color = "blue", size = "md" }) {
  const colorMap = {
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    green: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    red: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
    teal: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300",
    grape: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
    gray: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    yellow: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  };
  const sizeMap = { sm: "text-xs px-1.5 py-0.5", md: "text-xs px-2 py-1", lg: "text-sm px-2.5 py-1" };
  return (
    <span className={`inline-flex items-center font-semibold rounded-full ${colorMap[color] || colorMap.blue} ${sizeMap[size] || sizeMap.md}`}>
      {children}
    </span>
  );
}

function NumberInput({ value, onChange, min, max, step, style, className }) {
  return (
    <input
      type="number" value={value} min={min} max={max} step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      style={style}
      className={`h-11 px-2 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg text-center text-sm font-semibold bg-white dark:bg-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-orange-400 ${className || ""}`}
    />
  );
}

function Skeleton({ height = 20, width, radius = "md" }) {
  return <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded" style={{ height, width: width || "100%" }} />;
}

function Card({ children, className }) {
  return (
    <div className={`bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-4 ${className || ""}`}>
      {children}
    </div>
  );
}

function Alert({ children, color = "blue", icon, title, variant = "light", withCloseButton, onClose }) {
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200",
    red: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200",
    green: "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200",
    teal: "bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-950 dark:border-teal-800 dark:text-teal-200",
    orange: "bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-950 dark:border-orange-800 dark:text-orange-200",
  };
  return (
    <div className={`flex gap-3 border rounded-md p-3 ${colorMap[color] || colorMap.blue}`}>
      {icon && <span className="flex-shrink-0 mt-0.5">{icon}</span>}
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold text-sm mb-0.5">{title}</p>}
        <div className="text-sm">{children}</div>
      </div>
      {withCloseButton && (
        <button type="button" onClick={onClose} className="flex-shrink-0 opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
      )}
    </div>
  );
}

function ProgressBar({ value, color = "blue", size = "lg" }) {
  const colorMap = {
    blue: "bg-blue-500", red: "bg-red-500", cyan: "bg-cyan-500",
    violet: "bg-violet-500", grape: "bg-purple-500", orange: "bg-orange-500",
    yellow: "bg-yellow-400", teal: "bg-teal-500",
  };
  const heightMap = { sm: "h-1.5", md: "h-2", lg: "h-3", xl: "h-4" };
  return (
    <div className={`w-full bg-zinc-200 dark:bg-zinc-700 rounded-full ${heightMap[size] || heightMap.md} overflow-hidden`}>
      <div className={`${colorMap[color] || "bg-blue-500"} h-full rounded-full transition-all duration-300`} style={{ width: `${Math.min(value, 100)}%` }} />
    </div>
  );
}

// (Local Pagination removed — the shared components/common/PaginationBar is used instead.)

// Shown in place of the Buy-Credit checkout when the "globalShowBuyCreditTab" Setting is
// off — the tab stays reachable but the payment UI is replaced with a maintenance notice.
function PaymentMaintenanceNotice({ t }) {
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-6 sm:p-10 flex flex-col items-center text-center gap-3">
      <div className="w-14 h-14 rounded-2xl bg-orange-50 dark:bg-orange-950/30 text-primary flex items-center justify-center">
        <IconTools size={28} stroke={1.7} />
      </div>
      <h3 className="text-base sm:text-lg font-bold text-zinc-800 dark:text-zinc-100">
        {t("payments.maintenanceTitle") || "পেমেন্ট সিস্টেমে কাজ চলছে"}
      </h3>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md leading-relaxed">
        {t("payments.maintenanceMsg") ||
          "আন্তরিকভাবে দুঃখিত। সাময়িক অসুবিধার জন্য কিছুক্ষণ পর আবার চেষ্টা করুন। ধন্যবাদ।"}
      </p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function SettingsPage() {
  const { user, updateProfile, getCurrentUser, updateSettings } = useAuthStore();
  const { selectedOfficeSpace, updateOfficeSpace } = useOfficeSpaceStore();
  const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
  const isOperator = user?.role === "OPERATOR";
  const { t, i18n } = useTranslation();
  const isBn = i18n.language === "bn";
  const toBn = (num) => {
    if (!isBn) return String(num);
    return String(num).replace(/[0-9]/g, d => "০১২৩৪৫৬৭৮৯"[d]);
  };
  // "৬ মাস" / "১ বছর" / "২ বছর" — localized free-module duration.
  const formatFreeDuration = (months) => {
    if (!months) return '';
    if (months % 12 === 0) { const y = months / 12; return isBn ? `${toBn(y)} বছর` : `${y} year${y > 1 ? 's' : ''}`; }
    return isBn ? `${toBn(months)} মাস` : `${months} month${months > 1 ? 's' : ''}`;
  };
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [jobPosterOpen, setJobPosterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "photoeditsettings");
  // Transparent-output opt-in flows through a confirmation modal (face-change warning).
  const [showTransparentConfirm, setShowTransparentConfirm] = useState(false);
  const [transparentSaving, setTransparentSaving] = useState(false);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
  }, [searchParams]); // eslint-disable-line

  const VALID_TABS = new Set(["photoeditsettings", "general", "incomeexpense", "buycredit", "credits", "paymenthistory"]);
  useEffect(() => {
    if (!VALID_TABS.has(activeTab)) {
      setActiveTab("photoeditsettings");
      setSearchParams({ tab: "photoeditsettings" });
    }
  }, [activeTab]); // eslint-disable-line

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // ── Photo edit settings state ──────────────────────────────────────────
  const [defaultPrompts, setDefaultPrompts] = useState(DEFAULT_PROMPT_FLAGS);
  const [showSuitColorOptions, setShowSuitColorOptions] = useState(false);
  const [enabledDressStyles, setEnabledDressStyles] = useState(null);
  const [enabledDressColors, setEnabledDressColors] = useState(null);
  const [applyAiTemplateWatermark, setApplyAiTemplateWatermark] = useState(true);
  // Generate-image quality tiers — show the HD / Ultra HD generate buttons (both OFF by default).
  const [qualityHdEnabled, setQualityHdEnabled] = useState(false);
  const [qualityUltraEnabled, setQualityUltraEnabled] = useState(false);

  const DEFAULT_PRINT_SIZES = {
    passportSingleW: 41, passportSingleH: 50,
    passportDualW: 50, passportDualH: 41,
    stampSingleW: 20, stampSingleH: 25,
    stampDualW: 25, stampDualH: 20,
    size2RW: 64, size2RH: 89,
    size3RW: 89, size3RH: 127,
    size4RW: 102, size4RH: 152,
    size5RW: 127, size5RH: 178,
    size6RW: 152, size6RH: 203,
    ...PHOTO_PRINT_DEFAULTS,
  };
  const [printSizes, setPrintSizes] = useState(DEFAULT_PRINT_SIZES);
  // Ratio lock per print-size row (keyed by the row's wKey). Locked by default — editing
  // one side rescales the other; the user can unlock a row to edit W and H independently.
  const [ratioUnlocked, setRatioUnlocked] = useState({});
  // Last ratio we used per row, with the W/H it produced. If the values changed any other
  // way (reset, saved load, edited while unlocked) the entry no longer matches and the
  // ratio is re-read from the current W/H, so it never drifts from rounding.
  const ratioRef = useRef({});
  const changePrintDim = (item, axis, raw) => {
    const val = Number(raw);
    const own = axis === "w" ? item.wKey : item.hKey;
    const other = axis === "w" ? item.hKey : item.wKey;
    const ownDefault = axis === "w" ? item.defaultW : item.defaultH;
    if (!val) { setPrintSizes(prev => ({ ...prev, [own]: ownDefault })); return; }
    const next = { ...printSizes, [own]: val };
    if (!ratioUnlocked[item.wKey]) {
      const w0 = printSizes[item.wKey] ?? item.defaultW;
      const h0 = printSizes[item.hKey] ?? item.defaultH;
      let entry = ratioRef.current[item.wKey];
      if (!entry || entry.w !== w0 || entry.h !== h0) entry = { r: w0 / h0 };
      const o = axis === "w" ? val / entry.r : val * entry.r;
      next[other] = Math.min(500, Math.max(1, Math.round(o)));
      ratioRef.current[item.wKey] = { r: entry.r, w: next[item.wKey], h: next[item.hKey] };
    }
    setPrintSizes(next);
  };
  // Default photo gap (mm) applied in the print modal. Everyone starts at 3;
  // saved here it overrides that default for this user only.
  const DEFAULT_IMAGE_GAP = 3;
  const [defaultImageGap, setDefaultImageGap] = useState(DEFAULT_IMAGE_GAP);
  // Dedicated saving flag for the Print Size card's own Save button (independent of
  // the page-wide Save Changes button, which uses `loading`).
  const [savingPrintSizes, setSavingPrintSizes] = useState(false);
  // Generated-photo file-name pattern (prefix mode + date toggle) — see utils/photoFileName.js.
  const [photoNameSettings, setPhotoNameSettings] = useState(DEFAULT_PHOTO_NAME_SETTINGS);

  const ALL_DRESS_STYLES = [
    { value: "shirt",               label: t("imageGeneration.shirt"),              image: "/images/clothing-styles/shirt.svg" },
    { value: "t-shirt",             label: t("imageGeneration.tShirt"),             image: "/images/clothing-styles/t-shirt.svg" },
    { value: "suite",               label: t("imageGeneration.suite"),              image: "/images/clothing-styles/suite.svg" },
    { value: "suite-tieless",       label: t("imageGeneration.suiteTieless"),       image: "/images/clothing-styles/suite-tieless.svg" },
    { value: "panjbi",              label: t("imageGeneration.panjbi"),             image: "/images/clothing-styles/panjbi.svg" },
    { value: "tops",                label: t("imageGeneration.tops"),               image: "/images/clothing-styles/tops.svg" },
    { value: "round-collar-tshirt", label: t("imageGeneration.roundCollarTshirt"),  image: "/images/clothing-styles/round-collar-tshirt.svg" },
    { value: "sharee",              label: t("imageGeneration.sharee"),             image: "/images/clothing-styles/sharee.svg" },
    { value: "sharee-design",       label: t("imageGeneration.designSharee"),       image: "/images/clothing-styles/sharee-design.svg" },
    { value: "hizab",               label: t("imageGeneration.hizab"),              image: "/images/clothing-styles/hizab.svg" },
    { value: "kameez-hijab",        label: t("imageGeneration.kameezHijab"),        image: "/images/clothing-styles/kameez-hijab.svg" },
    { value: "shari-hijab",         label: t("imageGeneration.shariHijab"),         image: "/images/clothing-styles/shari-hijab.svg" },
    { value: "orna-kameez",         label: t("imageGeneration.ornaKameez"),         image: "/images/clothing-styles/orna-kameez.svg" },
    { value: "jacket",              label: t("imageGeneration.jacket"),             image: "/images/clothing-styles/jacket.svg" },
    { value: "abaya", label: t("imageGeneration.abaya"), image: "/images/clothing-styles/abaya.svg" },
    { value: "thobe", label: t("imageGeneration.thobe"), image: "/images/clothing-styles/thobe.svg" },
    { value: "thobe-ghutra", label: t("imageGeneration.thobeGhutra"), image: "/images/clothing-styles/thobe-ghutra.svg" },
    { value: "nehru-jacket", label: t("imageGeneration.nehruJacket"), image: "/images/clothing-styles/nehru-jacket.svg" },
    { value: "hijab-formal", label: t("imageGeneration.hijabFormalWear"), image: "/images/clothing-styles/hijab-formal.svg" },
  ];

  // ── Credit state ───────────────────────────────────────────────────────
  const [creditData, setCreditData] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPage, setCreditPage] = useState(1);
  const [creditStats, setCreditStats] = useState(null);
  const [creditCosts, setCreditCosts] = useState(null);
  const [creditConfig, setCreditConfig] = useState(null);
  // Per-tier effective credit cost (Normal/HD/Ultra) — shown beside the quality toggles.
  const [imageTierCosts, setImageTierCosts] = useState(null);
  const creditLabel = (c) => (c != null ? ` · ${isBn ? toBnDigits(c) : c} ${t("imageGeneration.quality.creditsShort") || "ক্রেডিট"}` : "");
  const [hasPaymentHistory, setHasPaymentHistory] = useState(false);

  // ── General tab state ──────────────────────────────────────────────────
  const toEnglishDigits = (str) => String(str ?? '').replace(/[০-৯]/g, (ch) => '০১২৩৪৫৬৭৮৯'.indexOf(ch));
  const sanitizeAmountInput = (val) => val.replace(/[^0-9০-৯.]/g, '');
  // Parse a price input (Bangla/English digits) to a Number, or null when blank/invalid.
  const parsePrice = (v) => {
    const s = toEnglishDigits(v).trim();
    if (s === '') return null;
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
  };
  // Suggestions are TYPE-AWARE — income (আয়) and expense (ব্যয়) keep separate lists.
  const [suggestionType, setSuggestionType] = useState('income');
  const [incomeSuggestions, setIncomeSuggestions] = useState([]);
  const [expenseSuggestions, setExpenseSuggestions] = useState([]);
  // Add-row inputs (name + optional price) for a new custom service.
  const [suggestionInput, setSuggestionInput] = useState('');
  const [suggestionPriceInput, setSuggestionPriceInput] = useState('');
  const [suggestionsSaving, setSuggestionsSaving] = useState(false);
  // Add-service-to-shop-profile (income names live on the business profile): the input,
  // its preset-suggestion dropdown, and a saving flag.
  const [serviceAddInput, setServiceAddInput] = useState('');
  const [serviceAddOpen, setServiceAddOpen] = useState(false);
  const [serviceSaving, setServiceSaving] = useState(false);

  // The list currently being edited (based on the active income/expense toggle).
  // Entries are {name, price, category} — modules (category set) have a fixed name
  // and can't be deleted; user entries (category null) are fully editable/removable.
  const activeSuggestions = suggestionType === 'income' ? incomeSuggestions : expenseSuggestions;
  const setActiveSuggestions = suggestionType === 'income' ? setIncomeSuggestions : setExpenseSuggestions;

  const normName = (s) => (s || '').normalize('NFC').trim().toLowerCase();

  // Add a custom service — skips blanks and name duplicates (case/Unicode-insensitive).
  const addSuggestion = () => {
    const name = suggestionInput.trim();
    if (!name) return;
    if (!activeSuggestions.some((s) => normName(s.name) === normName(name))) {
      setActiveSuggestions((prev) => [...prev, { name, price: parsePrice(suggestionPriceInput), category: null }]);
    }
    setSuggestionInput('');
    setSuggestionPriceInput('');
  };

  // Patch one entry in place. Module rows ignore name edits (name is fixed).
  const updateSuggestion = (index, patch) => {
    setActiveSuggestions((prev) => prev.map((s, i) => {
      if (i !== index) return s;
      const next = { ...s, ...patch };
      if (s.category && patch.name !== undefined) next.name = s.name; // lock module name
      return next;
    }));
  };

  // ── Rename a row (income list) — double-click the name or click the pencil ──────────
  // Works for the built-in ("অটো") modules AND the shop's own services. Enter / blur applies +
  // saves, Esc cancels. Modules: cleared → back to the default name. Shop services: the name
  // lives on the business profile, so it is renamed THERE (price carried over to the new name).
  const [renamingKey, setRenamingKey] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renamingRef = useRef(null); // { key, category, name }
  const RENAME_MAX = 80;
  const rowKey = (s) => s.category || `svc:${normName(s.name)}`;
  const startRename = (s) => {
    renamingRef.current = { key: rowKey(s), category: s.category || null, name: s.name };
    setRenamingKey(rowKey(s));
    setRenameValue(s.name);
  };
  const cancelRename = () => { renamingRef.current = null; setRenamingKey(null); };
  const commitRename = async () => {
    const target = renamingRef.current;
    if (!target) return; // already committed/cancelled (Enter then blur, or Esc)
    cancelRename();
    const current = incomeSuggestions.find((s) => rowKey(s) === target.key);
    if (!current) return;
    if (hasUnsafeContent(renameValue)) {
      notifications.show({ message: t('incomeExpense.nameInvalid') || 'এই নামটি ব্যবহার করা যাবে না', color: 'red' });
      return;
    }
    let next = sanitizeText(renameValue, { maxLen: RENAME_MAX }).normalize('NFC').trim();
    if (target.category) {
      const mod = INCOME_MODULES.find((m) => m.category === target.category);
      if (!mod) return;
      if (!next) next = mod.name; // cleared → back to the default name
    } else if (!next) {
      return; // a shop service can't be nameless — keep the old name
    }
    if (next === current.name) return;
    if (incomeSuggestions.some((s) => rowKey(s) !== target.key && normName(s.name) === normName(next))) {
      notifications.show({ message: t('incomeExpense.nameExists') || 'এই নামে আগে থেকেই একটি সার্ভিস আছে', color: 'orange' });
      return;
    }
    const updated = incomeSuggestions.map((s) => (rowKey(s) === target.key ? { ...s, name: next } : s));
    // Local state FIRST so the price follows the new name when the shop update triggers a reflow.
    setIncomeSuggestions(updated);
    if (!target.category) {
      const ok = await renameShopService(current.name, next);
      if (!ok) { setIncomeSuggestions(incomeSuggestions); return; } // revert the row
    }
    await persistSuggestions(updated, expenseSuggestions);
  };

  // Rename one entry of the active shop's businessProfile.services (position kept).
  const renameShopService = async (oldName, newName) => {
    if (!selectedOfficeSpace?.id || serviceSaving) return false;
    const existing = Array.isArray(selectedOfficeSpace?.businessProfile?.services)
      ? selectedOfficeSpace.businessProfile.services : [];
    if (!existing.some((x) => normName(x) === normName(oldName))) return false;
    setServiceSaving(true);
    try {
      await updateOfficeSpace(selectedOfficeSpace.id, {
        services: existing.map((x) => (normName(x) === normName(oldName) ? newName : x)),
      });
      return true;
    } catch (err) {
      notifications.show({ message: err.message || t('common.error') || 'নাম পরিবর্তন করা যায়নি', color: 'red' });
      return false;
    } finally {
      setServiceSaving(false);
    }
  };

  // Name cell for an income row: the text (or the inline rename input) + the pencil on the right.
  const renderNameCell = (s) => {
    const editing = renamingKey === rowKey(s);
    const isAuto = !!s.category;
    return (
      <div
        onDoubleClick={() => startRename(s)}
        title={t('incomeExpense.renameHint') || 'নাম বদলাতে ডাবল ক্লিক করুন'}
        className={`flex-1 min-w-0 flex items-center gap-1.5 h-9 pl-3 pr-1.5 rounded-lg border ${isAuto ? 'bg-zinc-50 dark:bg-zinc-800/60' : 'bg-white dark:bg-zinc-800'} ${editing ? 'border-primary ring-2 ring-primary/30' : 'border-zinc-200 dark:border-zinc-700'}`}>
        {editing ? (
          <input type="text" autoFocus value={renameValue} maxLength={RENAME_MAX}
            onChange={(e) => setRenameValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
            }}
            onBlur={commitRename}
            className="flex-1 min-w-0 bg-transparent text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 focus:outline-none" />
        ) : (
          <span className={`truncate text-[13px] text-zinc-700 dark:text-zinc-200 select-none ${isAuto ? 'font-semibold' : ''}`}>{s.name}</span>
        )}
        {isAuto && (
          <span className="flex-none text-[9px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded px-1 py-0.5">{t('incomeExpense.moduleTag') || 'অটো'}</span>
        )}
        {!editing && (
          <button type="button" onClick={() => startRename(s)} disabled={serviceSaving}
            title={t('incomeExpense.renameModule') || 'নাম পরিবর্তন করুন'}
            aria-label={t('incomeExpense.renameModule') || 'নাম পরিবর্তন করুন'}
            className="ml-auto flex-none w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50">
            <IconPencil size={14} />
          </button>
        )}
      </div>
    );
  };

  // Save both lists (also used by the rename above so Enter persists straight away).
  const persistSuggestions = async (incomeList = incomeSuggestions, expenseList = expenseSuggestions) => {
    setSuggestionsSaving(true);
    try {
      const legacyAmounts = {};
      for (const m of INCOME_MODULES) {
        const entry = incomeList.find((s) => s.category === m.category);
        legacyAmounts[m.legacyKey] = entry?.price ?? null;
      }
      await updateSettings({ incomeSuggestions: incomeList, expenseSuggestions: expenseList, ...legacyAmounts });
      notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "সংরক্ষিত হয়েছে", color: "green" });
      return true;
    } catch (err) {
      notifications.show({ message: err.message || "Failed to save", color: "red" });
      return false;
    } finally { setSuggestionsSaving(false); }
  };

  const removeSuggestion = (index) => {
    setActiveSuggestions((prev) => prev.filter((s, i) => i !== index || s.category)); // never drop modules
  };

  // Live snapshot of the income prices so the reflow effect below can keep any UNSAVED
  // price edits when the service list rebuilds (e.g. right after adding a service).
  const incomeSuggestionsRef = useRef([]);
  incomeSuggestionsRef.current = incomeSuggestions;

  // Add a service to the ACTIVE shop's business profile. Persisted via the office-space
  // update (partial merge — only `services` changes), which refreshes selectedOfficeSpace,
  // so the income list reflows to include the new service (unsaved prices are preserved).
  const addServiceToProfile = async (rawName) => {
    const name = (rawName || '').normalize('NFC').trim();
    if (!name || serviceSaving) return;
    if (!selectedOfficeSpace?.id) {
      notifications.show({ message: t('incomeExpense.noShopSelected') || 'কোনো দোকান নির্বাচিত নেই', color: 'red' });
      return;
    }
    const existing = Array.isArray(selectedOfficeSpace?.businessProfile?.services)
      ? selectedOfficeSpace.businessProfile.services : [];
    if (existing.some((s) => normName(s) === normName(name))) {
      setServiceAddInput(''); setServiceAddOpen(false);
      return; // already a service — nothing to add
    }
    setServiceSaving(true);
    try {
      await updateOfficeSpace(selectedOfficeSpace.id, { services: [...existing, name] });
      setServiceAddInput(''); setServiceAddOpen(false);
      notifications.show({ message: t('incomeExpense.serviceAdded') || 'সার্ভিস যোগ হয়েছে', color: 'green' });
    } catch (err) {
      notifications.show({ message: err.message || t('common.error') || 'যোগ করা যায়নি', color: 'red' });
    } finally {
      setServiceSaving(false);
    }
  };

  // Delete a shop service straight from here (income names live on the business profile).
  // Removes it from businessProfile.services via the partial-merge update, which reflows
  // the list; unsaved prices on the remaining rows are preserved.
  const removeServiceFromProfile = async (rawName) => {
    const name = (rawName || '').normalize('NFC').trim();
    if (!name || serviceSaving || !selectedOfficeSpace?.id) return;
    const existing = Array.isArray(selectedOfficeSpace?.businessProfile?.services)
      ? selectedOfficeSpace.businessProfile.services : [];
    const next = existing.filter((s) => normName(s) !== normName(name));
    if (next.length === existing.length) return; // not a shop service — nothing to remove
    setServiceSaving(true);
    try {
      await updateOfficeSpace(selectedOfficeSpace.id, { services: next });
      notifications.show({ message: t('incomeExpense.serviceRemoved') || 'সার্ভিস মুছে ফেলা হয়েছে', color: 'green' });
    } catch (err) {
      notifications.show({ message: err.message || t('common.error') || 'মুছে ফেলা যায়নি', color: 'red' });
    } finally {
      setServiceSaving(false);
    }
  };

  // Preset service suggestions shown on focus: the common-services catalog minus the ones
  // already in the list, filtered by the typed text.
  const presetServiceSuggestions = useMemo(() => {
    const q = normName(serviceAddInput);
    const inList = new Set(incomeSuggestions.map((s) => normName(s.name)));
    return COMMON_INCOME_SERVICES.filter((nm) => {
      const k = normName(nm);
      return !inList.has(k) && (!q || k.includes(q));
    }).slice(0, 12);
  }, [serviceAddInput, incomeSuggestions]);

  // ── Sync state from user ───────────────────────────────────────────────
  useEffect(() => {
    // INCOME names are authoritative from the ACTIVE shop's business profile: the AUTO
    // modules always lead, then the services the current shop picked. Prices persist
    // per-user (settings.incomeSuggestions, matched by name) so a service keeps its price;
    // shop services not yet priced show blank. Switching the active shop reflows this list.
    const built = buildSuggestionsForType(user?.settings, 'income');
    // Keep unsaved local price edits across a reflow: prefer the current in-state price,
    // then the saved price. (Modules keyed by category, shop services by name.)
    const cur = incomeSuggestionsRef.current || [];
    const localByCat = new Map(cur.filter((s) => s.category).map((s) => [s.category, s]));
    const localByName = new Map(cur.filter((s) => !s.category).map((s) => [normName(s.name), s.price]));
    const modules = built.filter((s) => s.category).map((s) => ({ ...s, price: localByCat.has(s.category) ? localByCat.get(s.category).price : s.price, name: localByCat.get(s.category)?.name || s.name }));
    const priceByName = new Map(built.filter((s) => !s.category).map((s) => [normName(s.name), s.price]));
    const shopServices = Array.isArray(selectedOfficeSpace?.businessProfile?.services)
      ? selectedOfficeSpace.businessProfile.services : [];
    const incomeList = [...modules];
    for (const svc of shopServices) {
      const nm = (svc || '').normalize('NFC').trim();
      const key = normName(nm);
      if (nm && !incomeList.some((s) => normName(s.name) === key)) {
        const price = localByName.has(key) ? localByName.get(key) : (priceByName.has(key) ? priceByName.get(key) : null);
        incomeList.push({ name: nm, price, category: null });
      }
    }
    setIncomeSuggestions(incomeList);
    setExpenseSuggestions(buildSuggestionsForType(user?.settings, 'expense'));
  }, [user?.settings?.defaultImageGenAmount, user?.settings?.defaultDocGenAmount, user?.settings?.defaultAiTemplateAmount, user?.settings?.defaultManualEditAmount, user?.settings?.defaultPrintMediaAmount, user?.settings?.incomeSuggestions, user?.settings?.expenseSuggestions, selectedOfficeSpace?.businessProfile?.services]); // eslint-disable-line

  // ── Photo-edit tab: what is on screen vs what is saved ─────────────────
  // The saved copy of every photo-edit setting. A Save button is enabled only while the staged values differ
  // from this, and goes back to disabled as soon as a save lands and the user record updates.
  const savedPhotoEdit = useMemo(() => ({
    defaultPrompts: { ...DEFAULT_PROMPT_FLAGS, ...(user?.defaultPrompts || {}) },
    showSuitColorOptions: !!user?.settings?.showSuitColorOptions,
    enabledDressStyles: user?.settings?.enabledDressStyles?.length ? user.settings.enabledDressStyles : null,
    enabledDressColors: user?.settings?.enabledDressColors?.length ? user.settings.enabledDressColors : null,
    applyAiTemplateWatermark: user?.settings?.applyAiTemplateWatermark !== undefined ? !!user.settings.applyAiTemplateWatermark : true,
    qualityHdEnabled: !!user?.settings?.qualityHdEnabled,
    qualityUltraEnabled: !!user?.settings?.qualityUltraEnabled,
    printSizes: { ...DEFAULT_PRINT_SIZES, ...(user?.settings?.printSizes || {}) },
    defaultImageGap: user?.settings?.defaultImageGap != null ? Number(user.settings.defaultImageGap) : DEFAULT_IMAGE_GAP,
    photoNameSettings: { ...DEFAULT_PHOTO_NAME_SETTINGS, ...(user?.settings?.photoNameSettings || {}) },
  }), [user?.defaultPrompts, user?.settings]); // eslint-disable-line
  const stagedPhotoEdit = { defaultPrompts, showSuitColorOptions, enabledDressStyles, enabledDressColors, applyAiTemplateWatermark, qualityHdEnabled, qualityUltraEnabled, printSizes, defaultImageGap, photoNameSettings };

  // Fill the tab from the saved copy when it arrives, and follow the saved copy again whenever it changes while
  // nothing is staged (e.g. the /me refresh landing after the page opened). Edits in progress are never overwritten.
  const [photoEditReady, setPhotoEditReady] = useState(false);
  const lastSavedPhotoEdit = useRef(null);
  useEffect(() => {
    if (!user) return;
    const prev = lastSavedPhotoEdit.current;
    lastSavedPhotoEdit.current = savedPhotoEdit;
    if (prev && (sameData(prev, savedPhotoEdit) || !photoEditSame(prev, stagedPhotoEdit, user?.country))) return;
    setDefaultPrompts(savedPhotoEdit.defaultPrompts);
    setShowSuitColorOptions(savedPhotoEdit.showSuitColorOptions);
    setEnabledDressStyles(savedPhotoEdit.enabledDressStyles);
    setEnabledDressColors(savedPhotoEdit.enabledDressColors);
    setApplyAiTemplateWatermark(savedPhotoEdit.applyAiTemplateWatermark);
    setQualityHdEnabled(savedPhotoEdit.qualityHdEnabled);
    setQualityUltraEnabled(savedPhotoEdit.qualityUltraEnabled);
    setPrintSizes(savedPhotoEdit.printSizes);
    setDefaultImageGap(savedPhotoEdit.defaultImageGap);
    setPhotoNameSettings(savedPhotoEdit.photoNameSettings);
    setPhotoEditReady(true);
  }, [savedPhotoEdit, user?.id]); // eslint-disable-line
  // Page-wide Save (everything on the tab) and the Print Size card's own Save (print sizes + photo gap only).
  const photoEditDirty = photoEditReady && !photoEditSame(stagedPhotoEdit, savedPhotoEdit, user?.country);
  const printSizesDirty = photoEditReady && (!sameData(printSizes, savedPhotoEdit.printSizes) || defaultImageGap !== savedPhotoEdit.defaultImageGap);

  // Income / expense suggestion lists: unsaved edits (prices, rows added or removed) vs the saved copy.
  const savedIncomeList = useMemo(
    () => buildShopServiceList(user?.settings, selectedOfficeSpace?.businessProfile),
    [user?.settings, selectedOfficeSpace?.businessProfile]
  );
  const savedExpenseList = useMemo(() => buildSuggestionsForType(user?.settings, 'expense'), [user?.settings]);
  const suggestionsDirty = incomeSuggestions.length > 0 && (
    !sameData(incomeSuggestions.map(plainSuggestion), savedIncomeList.map(plainSuggestion)) ||
    !sameData(expenseSuggestions.map(plainSuggestion), savedExpenseList.map(plainSuggestion))
  );

  useEffect(() => {
    getCurrentUser().catch(() => {});
    usageService.getCreditConfig().then(setCreditConfig).catch(() => {});
    usageService.getCreditCosts().then((r) => setImageTierCosts((r?.data || r)?.imageTiers || null)).catch(() => {});
  }, [getCurrentUser]);

  useEffect(() => {
    const checkPayments = async () => {
      if (user?.role === "OPERATOR") {
        if (!selectedOfficeSpace?.id) return;
        const result = await paymentService.getOwnerHasPayment(selectedOfficeSpace.id);
        setHasPaymentHistory(!!result?.hasPayment);
        return;
      }
      const data = await paymentService.getHistory({ page: 1, limit: 50 });
      const transactions = data?.transactions || [];
      setHasPaymentHistory(transactions.some(tx => tx.status === "SUCCESS"));
    };
    checkPayments().catch(() => {});
  }, [user?.id, user?.role, selectedOfficeSpace?.id]);

  useEffect(() => {
    if (activeTab === "credits") {
      const fetchCredits = async () => {
        try {
          setCreditLoading(true);
          const [data, stats, costs] = await Promise.all([
            usageService.getCreditHistory({ page: creditPage, days: 7 }),
            usageService.getCreditStats(),
            usageService.getCreditCosts(),
          ]);
          setCreditData(data);
          setCreditStats(stats);
          setCreditCosts(costs);
        } catch (error) {
          console.error("Failed to fetch credit history:", error);
        } finally {
          setCreditLoading(false);
        }
      };
      fetchCredits();
    }
  }, [activeTab, creditPage]);

  // Which payment gateway is live — decides which single buy-credit tab OWNERs see.
  const activeGateway = creditConfig?.activePaymentGateway || DEFAULT_PAYMENT_GATEWAY;

  // Bangladesh users (country BD or unset) buy via the BD gateways (Moneybag/SSLCommerz,
  // chosen by activePaymentGateway). Everyone else buys via FastSpring (international/USD).
  const isBdUser = !user?.country || user.country.toUpperCase() === 'BD';

  // Master kill-switch for the Buy Credit tab, from the "globalShowBuyCreditTab" Setting
  // (exposed via GET /api/credits/config). Shown by default; hidden only when explicitly
  // false — so the tab doesn't flicker away while the config request is still in flight.
  const showBuyCreditTab = creditConfig?.globalShowBuyCreditTab !== false;

  // Single buy-credit tab, gateway chosen by region. BD users (country BD or unset) buy the
  // `bangladesh`-type packages in BDT through whichever BD gateway is live (activePaymentGateway:
  // Moneybag or SSLCommerz). Everyone else buys the `others`-type packages in USD via FastSpring.
  // The gateway is derived here — not from the URL — so a non-BD user can never reach the BDT
  // gateway (and its BDT-priced packages) by hand-editing ?tab=. The BuyCreditTab component
  // filters packages by the same region rule, so currency + packages always stay consistent.
  const buyGateway = !isBdUser
    ? "fastspring"
    : (activeGateway === "sslCommerz" ? "sslcommerz" : "moneybag");

  // Migration nudge disabled — the Credits tab now always shows the per-action cost cards.
  // Previously it showed EITHER the migration banner OR the cost cards based on `showMigration`.
  // Kept commented for easy re-enable:
  // const legacyProfileCredit = user?.legacyCredit ?? (user?.migrated ? 0 : (user?.credit ?? 0));
  // const showMigration = !user?.migrated && legacyProfileCredit > 0;

  // Owner's split balance — drives how each per-action cost card renders in the Credits tab:
  //   • holds BOTH legacy + wallet → old→new comparison (struck-through old rate → new rate)
  //   • only legacy (wallet 0/null) → old cost only
  //   • only wallet (legacy 0/null) → new cost only  (default)
  const { legacy: legacyBal, wallet: walletBal } = creditParts(user);
  const showOldCost = legacyBal > 0;
  const showNewCost = walletBal > 0;

  // ── Build tab list ─────────────────────────────────────────────────────
  const tabList = [
    ...(user?.role !== "admin" ? [{ value: "general", label: t("profile.general") || "General", icon: <IconSettings size={16} /> }] : []),
    { value: "photoeditsettings", label: t("profile.defaultPrompts"), icon: <IconSparkles size={16} /> },
    ...(user?.role !== "admin" ? [{ value: "incomeexpense", label: t("settings.incomeExpenseTab") || "আয় ব্যয় হিসেব", icon: <IconReceipt2 size={16} /> }] : []),
    // Single buy-credit tab for every OWNER — the gateway (BD: Moneybag/SSLCommerz, else
    // FastSpring) is picked by region + activePaymentGateway inside the tab, not by the URL.
    // The tab ALWAYS shows; the "globalShowBuyCreditTab" Setting no longer hides it —
    // when off, the tab's content is swapped for a maintenance notice (see below).
    ...(user?.role === "OWNER"
      ? [{ value: "buycredit", label: t("payments.buyTitle"), icon: <IconShoppingCart size={16} /> }] : []),
    ...((user?.role === "OWNER" || user?.role === "admin") ? [{ value: "credits", label: t("settings.creditUsage") || "ক্রেডিট খরচ", icon: <IconCoins size={16} /> }] : []),
    ...(user?.role === "OWNER" ? [{ value: "paymenthistory", label: t("settings.paymentHistory") || "পেমেন্ট হিস্ট্রি", icon: <IconReceipt size={16} /> }] : []),
  ];

  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="font-hind pt-2 sm:pt-6 px-1 sm:px-2 pb-14">
      {/* ── Header ── */}
      <div className="mb-[18px]">
        <h1 className="m-0 text-[26px] sm:text-[30px] font-bold text-zinc-800 dark:text-zinc-100">
          {t("settings.title") || "সেটিংস"}
        </h1>
        <div className="text-[14.5px] text-zinc-500 dark:text-zinc-400 mt-1">
          {t("settings.subtitle") || "ফটো এডিট, ক্রেডিট ও সিস্টেম কনফিগারেশন"}
        </div>
      </div>

      {/* ── Tab bar (pill style) ── */}
      <div className="flex gap-2.5 flex-nowrap sm:flex-wrap overflow-x-auto pb-2 mb-[22px] [scrollbar-width:none] [-ms-overflow-style:none]">
        {tabList.map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleTabChange(tab.value)}
              className={`flex items-center gap-2 px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap shrink-0 border transition-colors ${
                isActive
                  ? "font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900"
                  : "font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              {tab.icon && <span className="shrink-0">{tab.icon}</span>}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── Photo edit settings tab ──                                     */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "photoeditsettings" && (
        // Visual order via `order-*`: Print Size + Photo Name first, then dress/prompt
        // sections, then Save last.
        <div className="flex flex-col gap-4">
          {/* Customer photo collection (permanent QR). order-none (=0) + DOM
              position first → renders at the TOP of the tab,
              above the print-size / transparent-output cards. */}
          {/* Hidden for platform admins — the photo booth is a shop-owner feature. */}
          {selectedOfficeSpace?.id && user?.role !== "admin" && (
            <div className="order-none">
              <PhotoCollectorSettings
                officeSpaceId={selectedOfficeSpace.id}
                shopName={selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name}
                shopLogo={selectedOfficeSpace?.businessProfile?.shopLogo}
                shopPhone={selectedOfficeSpace?.businessProfile?.shopPhone}
                shopAddress={selectedOfficeSpace?.businessProfile?.businessAddress}
                businessOpenTime={selectedOfficeSpace?.businessProfile?.openTime}
                businessCloseTime={selectedOfficeSpace?.businessProfile?.closeTime}
              />
            </div>
          )}

          {/* AI photo QUALITY tiers — enable the HD / Ultra HD generate buttons on the
              generate-image page. `order-none` (=0) + DOM position right after the photo
              booth → renders SECOND, directly under the booth card. */}
          <div className="order-none bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-base font-bold text-zinc-800 dark:text-zinc-100">
                {t("settings.qualityButtonsTitle") || "ছবির কোয়ালিটি অপশন"}
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                <img src="/module-icons/ai-tools.svg" alt="" className="w-3.5 h-3.5" />
                (AI Photo Editor)
              </span>
            </div>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-4">
              {t("settings.qualityButtonsDesc")}
            </p>
            <div className="flex items-center gap-3">
              <Switch checked={qualityHdEnabled} onChange={async (e) => {
                const v = e.currentTarget.checked;
                setQualityHdEnabled(v);
                try {
                  await updateSettings({ qualityHdEnabled: v });
                  notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "Settings saved", color: "green" });
                } catch (err) {
                  setQualityHdEnabled(!v); // revert on failure
                  notifications.show({ title: t("common.error"), message: err.message, color: "red" });
                }
              }} size="md" />
              <div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t("settings.enableHdQuality")}<span className="text-violet-600 dark:text-violet-400 font-semibold">{creditLabel(imageTierCosts?.hd)}</span></p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500">{t("settings.enableHdQualityHelp")}</p>
              </div>
            </div>
            {AI_PHOTO_ULTRA_HD && <div className="mt-4 flex items-center gap-3">
              <Switch checked={qualityUltraEnabled} onChange={async (e) => {
                const v = e.currentTarget.checked;
                setQualityUltraEnabled(v);
                try {
                  await updateSettings({ qualityUltraEnabled: v });
                  notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "Settings saved", color: "green" });
                } catch (err) {
                  setQualityUltraEnabled(!v); // revert on failure
                  notifications.show({ title: t("common.error"), message: err.message, color: "red" });
                }
              }} size="md" />
              <div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t("settings.enableUltraQuality")}<span className="text-amber-600 dark:text-amber-400 font-semibold">{creditLabel(imageTierCosts?.ultra)}</span></p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500">{t("settings.enableUltraQualityHelp")}</p>
              </div>
            </div>}
          </div>

          {/* AI photo output mode: OFF (default) = background baked into the image
              (best face likeness); ON = transparent cut-out master with instant,
              free background recolouring (slightly more face drift). Enabling goes
              through a confirmation modal; disabling (back to the safe default) is
              direct. order-2 + earlier DOM position → renders right ABOVE the
              photo-name card (also order-2) and below the order-1 print-size card. */}
          <div className="order-2">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm px-4 sm:px-[22px] py-[18px] flex items-center gap-4 sm:gap-[18px]">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t("profile.transparentOutput") || "Transparent AI photo output"}</p>
                {/* Red: this is a face-change trade-off warning, not a neutral hint */}
                <p className="mt-1 text-xs leading-relaxed text-red-600 dark:text-red-400">{t("profile.transparentOutputDesc") || "When ON, you can change the photo background anytime for free — but the face may change slightly. Keep it OFF to keep the face just like the original photo."}</p>
              </div>
              {user?.settings?.transparentImageOutput === true ? (
                <div className="flex-none flex items-center gap-3">
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{t("profile.transparentOutputOn") || "চালু আছে"}</span>
                  <button
                    onClick={async () => {
                      try {
                        await updateSettings({ transparentImageOutput: false });
                        notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "Settings saved", color: "green" });
                      } catch (err) { notifications.show({ title: t("common.error"), message: err.message, color: "red" }); }
                    }}
                    className="px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    {t("profile.transparentOutputDisable") || "বন্ধ করুন"}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowTransparentConfirm(true)}
                  className="flex-none px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg"
                >
                  {t("profile.transparentOutputEnable") || "চালু করুন"}
                </button>
              )}
            </div>

            <Dialog open={showTransparentConfirm} onOpenChange={setShowTransparentConfirm}>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle>{t("profile.transparentOutputConfirmTitle") || "ট্রান্সপারেন্ট আউটপুট চালু করবেন?"}</DialogTitle>
                  <DialogDescription className="text-red-600 dark:text-red-400 leading-relaxed">
                    {t("profile.transparentOutputConfirmMsg") || "আপনি কি সত্যিই এই ফিচারটি চালু করতে চান? চালু করলে ছবির ব্যাকগ্রাউন্ড ফ্রি-তে বদলানো যাবে, তবে AI পুরো ছবি নতুন করে আঁকে বলে চেহারা একটু বদলে যেতে পারে।"}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => setShowTransparentConfirm(false)}
                    className="px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    {t("common.cancel") || "বাতিল"}
                  </button>
                  <button
                    disabled={transparentSaving}
                    onClick={async () => {
                      setTransparentSaving(true);
                      try {
                        await updateSettings({ transparentImageOutput: true });
                        notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "Settings saved", color: "green" });
                        setShowTransparentConfirm(false);
                      } catch (err) {
                        notifications.show({ title: t("common.error"), message: err.message, color: "red" });
                      } finally { setTransparentSaving(false); }
                    }}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg disabled:opacity-60"
                  >
                    {t("profile.transparentOutputConfirmYes") || "হ্যাঁ, চালু করুন"}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Dress Styles Card */}
          <div className="order-3 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            <p className="text-base font-bold text-zinc-800 dark:text-zinc-100 mb-1">
              {t("profile.chooseDress") || "পোশাক নির্বাচন"}
            </p>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-4">
              {t("profile.chooseDressDesc") || "জেনারেট ইমেজে কোন পোশাক রাখতে চান তা নির্বাচন করুন।"}
            </p>
            {/* Auto-fill grid: tiles never shrink below 88px (96px from sm) — they wrap to
                another row instead of squeezing all styles into one cramped line. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 sm:gap-2.5 pb-1">
              {ALL_DRESS_STYLES.map(style => {
                const isEnabled = isDressStyleEnabled(enabledDressStyles, style.value, user?.country);
                return (
                  <button
                    key={style.value}
                    type="button"
                    onClick={() => setEnabledDressStyles(toggleDressStyle(enabledDressStyles, ALL_DRESS_STYLES.map(s => s.value), style.value, user?.country))}
                    className={`relative flex flex-col items-center justify-center rounded-xl border-2 py-3 px-2 transition-all hover:shadow-sm min-w-0 ${
                      isEnabled
                        ? "border-orange-400 bg-orange-50/50 dark:bg-orange-950/20"
                        : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
                    }`}
                  >
                    <DressPreviewSvg src={style.image} alt={style.label} className="w-12 h-12 sm:w-14 sm:h-14 object-contain mb-2" />
                    <span className={`text-center text-xs leading-tight ${isEnabled ? "text-orange-600 dark:text-orange-400 font-semibold" : "text-zinc-400 dark:text-zinc-500"}`}>
                      {style.label}
                    </span>
                    {isEnabled && (
                      <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-orange-400 flex items-center justify-center">
                        <IconCheck size={11} color="white" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dress Colors Card */}
          <div className="order-4 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            <p className="text-base font-bold text-zinc-800 dark:text-zinc-100 mb-1">
              {t("profile.chooseDressColors") || "পোশাকের রং"}
            </p>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-4">
              {t("profile.chooseDressColorsDesc") || "জেনারেটর সময় ব্যবহারযোগ্য রংগুলো নির্বাচন করুন।"}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {ALL_DRESS_COLORS.map(color => {
                const isEnabled = !enabledDressColors?.length || enabledDressColors.includes(color.id);
                const isLight = ["white", "beige", "yellow", "gold"].includes(color.id);
                return (
                  <Tip key={color.id} label={t(color.i18nKey) || color.colorLabel}>
                    <button
                      type="button"
                      onClick={() => {
                        const currentEnabled = enabledDressColors?.length
                          ? [...enabledDressColors]
                          : ALL_DRESS_COLORS.map(c => c.id);
                        const newEnabled = isEnabled
                          ? currentEnabled.filter(v => v !== color.id)
                          : [...currentEnabled, color.id];
                        setEnabledDressColors(newEnabled.length === ALL_DRESS_COLORS.length ? null : newEnabled);
                      }}
                      className="relative rounded-lg transition-all flex items-center justify-center"
                      style={{
                        width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
                        background: color.hex,
                        border: isEnabled ? "3px solid #fe9a00" : `2px solid ${isLight ? "#ddd" : "transparent"}`,
                        opacity: isEnabled ? 1 : 0.35,
                      }}
                    >
                      {isEnabled && <IconCheck size={16} color={isLight ? "#555" : "white"} strokeWidth={3} />}
                    </button>
                  </Tip>
                );
              })}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Switch checked={showSuitColorOptions} onChange={(e) => setShowSuitColorOptions(e.currentTarget.checked)} size="md" />
              <div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t("profile.suitColorOptionsLabel") || "পোশাকের বিকল্প রং নির্বাচন"}</p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500">{t("profile.suitColorOptionsDesc") || "যেমন ব্লাউজ, টাই, ওড়নার রং ইত্যাদি।"}</p>
              </div>
            </div>
          </div>

          {/* Default prompts Card */}
          <div className="order-5 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            <p className="text-base font-bold text-zinc-800 dark:text-zinc-100 mb-1">{t("profile.additionalInstructions")}</p>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-4">{t("profile.defaultPromptsHint")}</p>
            {/* 5 columns only from xl up — each toggle now carries an icon, so narrower screens get fewer, wider cells. */}
            <div className="grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[
                // Full set — must stay in sync with the editor's tool grid
                // (GenerateImage.jsx `dp` defaults). Grouped by kind for readability.
                { key: "removeGlasses", label: t("imageGeneration.removeGlass") },
                { key: "keepSunGlasses", label: t("imageGeneration.keepSunGlasses") },
                { key: "glowMakeup", label: t("imageGeneration.glowMakeup") },
                { key: "smoothSkin", label: t("imageGeneration.smoothSkin") },
                { key: "brightenImage", label: t("imageGeneration.brightenImage") },
                { key: "studioLighting", label: t("imageGeneration.studioLighting") },
                { key: "removeStrayHair", label: t("imageGeneration.removeStrayHair") },
                { key: "applyHair", label: t("imageGeneration.applyHair") },
                { key: "removeBeard", label: t("imageGeneration.removeBeard") },
                { key: "addBeard", label: t("imageGeneration.addBeard") },
                { key: "keepBeard", label: t("imageGeneration.keepBeard") },
                { key: "keepMustache", label: t("imageGeneration.keepMustache") },
                { key: "keepFacialMarks", label: t("imageGeneration.keepFacialMarks") },
                { key: "openEyes", label: t("imageGeneration.openEyes") },
                { key: "closeMouth", label: t("imageGeneration.closeMouth") },
                { key: "lookAtCamera", label: t("imageGeneration.lookAtCamera") },
                { key: "straightenHead", label: t("imageGeneration.straightenHead") },
                { key: "removeTupi", label: t("imageGeneration.removeTupi") },
                { key: "addTupi", label: t("imageGeneration.addTupi") },
                { key: "showEar", label: t("imageGeneration.showEar") },
                { key: "keepSchoolDressDetails", label: t("imageGeneration.keepSchoolDressDetails") },
                { key: "keepNamePlate", label: t("imageGeneration.keepNamePlate") },
                { key: "showCustomInstruction", label: t("imageGeneration.enableCustomInstruction") },
              ].map(({ key, label }) => (
                <Switch key={key} label={label} icon={PHOTO_TOOL_ICONS[key]} size="sm" checked={!!defaultPrompts[key]} onChange={(e) => { const checked = e.currentTarget.checked; setDefaultPrompts(prev => ({ ...prev, [key]: checked })); }} />
              ))}
            </div>
          </div>

          {/* Print size Card */}
          <div className="order-1 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            {(() => {
              const mmToInch = (mm) => Math.round(mm / 25.4 * 100) / 100;
              const GROUPS = [
                { label: t("printSize.passport"), color: "orange", items: [
                  { label: t("printSize.single"), wKey: "passportSingleW", hKey: "passportSingleH", defaultW: 41, defaultH: 50, desc: t("printSize.passportSingleDesc") },
                  { label: t("printSize.dual"), wKey: "passportDualW", hKey: "passportDualH", defaultW: 50, defaultH: 41, desc: t("printSize.passportDualDesc") },
                ]},
                { label: t("printSize.stamp"), color: "gray", items: [
                  { label: t("printSize.single"), wKey: "stampSingleW", hKey: "stampSingleH", defaultW: 20, defaultH: 25, desc: t("printSize.stampSingleDesc") },
                  { label: t("printSize.dual"), wKey: "stampDualW", hKey: "stampDualH", defaultW: 25, defaultH: 20, desc: t("printSize.stampDualDesc") },
                ]},
                { label: t("printSize.rSize"), color: "teal", items: [
                  { label: "2R", wKey: "size2RW", hKey: "size2RH", defaultW: 64, defaultH: 89, desc: t("printSize.r2Desc") },
                  { label: "3R", wKey: "size3RW", hKey: "size3RH", defaultW: 89, defaultH: 127, desc: t("printSize.r3Desc") },
                  { label: "4R", wKey: "size4RW", hKey: "size4RH", defaultW: 102, defaultH: 152, desc: t("printSize.r4Desc") },
                  { label: "5R", wKey: "size5RW", hKey: "size5RH", defaultW: 127, defaultH: 178, desc: t("printSize.r5Desc") },
                  { label: "6R", wKey: "size6RW", hKey: "size6RH", defaultW: 152, defaultH: 203, desc: t("printSize.r6Desc") },
                ]},
                ...(isBdUser ? [{ label: t("printSize.documents"), color: "grape", items: [
                  { ...photoPrintSizeOf("bd_epassport"), label: t("imageGeneration.abbrEPass"), desc: t("printSize.ePassDesc") },
                  { ...photoPrintSizeOf("bd_birth"), label: t("imageGeneration.abbrBirth"), desc: t("printSize.birthDesc") },
                ]}] : []),
                { label: t("imageGeneration.abbrVisa"), color: "violet", items: VISA_GROUPS.map((visa) => ({
                  ...photoPrintSizeOf(visa.key),
                  label: isBn ? visa.sizeBn : visa.size,
                  desc: isBn ? visa.countries.slice(0, 3).join(", ") : t("printSize.visaDesc"),
                })) },
              ];
              return (
                <div>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 pr-3">
                      <p className="text-base font-bold text-zinc-800 dark:text-zinc-100">{t("profile.printSizeCustomize") || "Print Size Customize"}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{t("profile.printSizeCustomizeDesc") || "Set the default image sizes used in the print modal."}</p>
                    </div>
                    <Tip label={t("printSize.resetTooltip")}>
                      <button type="button" onClick={() => setPrintSizes(DEFAULT_PRINT_SIZES)} className="p-1.5 rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-500"><IconRefresh size={16} /></button>
                    </Tip>
                  </div>
                  {GROUPS.map((group, gIdx) => (
                    <div key={group.label} className="py-3" style={{ borderBottom: gIdx < GROUPS.length - 1 ? "1px solid #e4e4e7" : "none" }}>
                      <Badge color={group.color} size="sm">{group.label}</Badge>
                      <div className="mt-2.5 grid gap-2 sm:gap-3" style={{ gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)" }}>
                        {group.items.map((item) => {
                          const w = printSizes[item.wKey] ?? item.defaultW;
                          const h = printSizes[item.hKey] ?? item.defaultH;
                          return (
                            <div key={item.wKey}>
                              <p className="text-xs font-bold mb-1 text-zinc-700 dark:text-zinc-300">{item.label}</p>
                              <div className="flex items-center gap-1 flex-wrap mb-1">
                                <div className="flex items-center gap-1 flex-nowrap">
                                  <NumberInput value={w} min={1} max={500} step={1} onChange={(val) => changePrintDim(item, "w", val)} style={{ width: isMobile ? 58 : 72 }} />
                                  <Tip label={ratioUnlocked[item.wKey] ? t("printSize.lockRatio") : t("printSize.unlockRatio")}>
                                    <button
                                      type="button"
                                      aria-pressed={!ratioUnlocked[item.wKey]}
                                      aria-label={ratioUnlocked[item.wKey] ? t("printSize.lockRatio") : t("printSize.unlockRatio")}
                                      onClick={() => setRatioUnlocked(prev => ({ ...prev, [item.wKey]: !prev[item.wKey] }))}
                                      className={`w-6 h-6 flex items-center justify-center rounded-md border transition-colors ${ratioUnlocked[item.wKey] ? "border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800" : "border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400"}`}
                                    >
                                      {ratioUnlocked[item.wKey] ? <IconLinkOff size={14} /> : <IconLink size={14} />}
                                    </button>
                                  </Tip>
                                  <NumberInput value={h} min={1} max={500} step={1} onChange={(val) => changePrintDim(item, "h", val)} style={{ width: isMobile ? 58 : 72 }} />
                                  <span className="text-xs text-zinc-400">mm</span>
                                </div>
                                {!isMobile && <span className="text-zinc-400 whitespace-nowrap" style={{ fontSize: 10 }}>({mmToInch(w)}" × {mmToInch(h)}")</span>}
                              </div>
                              {!isMobile && <p className="text-zinc-400 leading-snug" style={{ fontSize: 10 }}><span className="font-semibold">{t("printSize.default")}</span> {item.defaultW}×{item.defaultH}mm — {item.desc}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Default photo gap (mm) — the print modal's starting value */}
                  <div className="pt-4 mt-1" style={{ borderTop: "1px solid #e4e4e7" }}>
                    <div className="flex justify-between items-start mb-2.5">
                      <div className="flex-1 pr-3">
                        <p className="text-sm font-bold text-zinc-700 dark:text-zinc-300">{t("profile.imageGapDefault") || "Photo Gap (Default)"}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{t("profile.imageGapDefaultDesc") || "The default spacing between photos in the print modal."}</p>
                      </div>
                      <Tip label={t("printSize.resetTooltip")}>
                        <button type="button" onClick={() => setDefaultImageGap(DEFAULT_IMAGE_GAP)} className="p-1.5 rounded-md border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-500"><IconRefresh size={16} /></button>
                      </Tip>
                    </div>
                    <div className="max-w-md">
                      <div className="mb-1.5 text-xs font-semibold text-zinc-500">
                        {t("imageGeneration.imageGap") || "Image Gap (mm)"}: <b className="text-zinc-700 dark:text-zinc-300">{toBn(defaultImageGap)}mm</b>
                      </div>
                      <input
                        type="range" min={0} max={10} step={0.5}
                        value={defaultImageGap}
                        onChange={(e) => setDefaultImageGap(Number(e.target.value))}
                        className="w-full accent-orange-500"
                      />
                      <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
                        <span>{toBn(0)}</span><span>{toBn(5)}</span><span>{toBn(10)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Save just the print sizes + default gap */}
                  <div className="pt-4 mt-1 flex justify-end">
                    <SaveButton
                      dirty={printSizesDirty} saving={savingPrintSizes}
                      onClick={async () => {
                        setSavingPrintSizes(true);
                        try {
                          await updateSettings({ printSizes, defaultImageGap });
                          notifications.show({ title: t("common.success"), message: t("profile.settingsSaved"), color: "green" });
                        } catch (err) {
                          notifications.show({ title: t("common.error"), message: err.message, color: "red" });
                        } finally { setSavingPrintSizes(false); }
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Photo file-name pattern Card */}
          <div className="order-2 bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6">
            {(() => {
              const shopName = (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || "").trim() || "Sohozkaj";
              const prefix = buildPhotoPrefix(shopName, photoNameSettings);
              const today = new Date().toISOString().slice(0, 10);
              const MODES = [
                { value: "full", label: t("photoNameSettings.modeFull"), example: shopName },
                { value: "short", label: t("photoNameSettings.modeShort"), example: shopInitials(shopName) || shopName },
                { value: "custom", label: t("photoNameSettings.modeCustom"), example: t("photoNameSettings.modeCustomHint") },
              ];
              return (
                <div>
                  <p className="text-base font-bold text-zinc-800 dark:text-zinc-100">{t("photoNameSettings.title")}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{t("photoNameSettings.desc")}</p>

                  <div className="mt-3 grid gap-4 md:gap-6 items-start" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                  {/* LEFT — prefix mode: compact radio group (pick ONE) */}
                  <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden divide-y divide-zinc-200 dark:divide-zinc-700">
                    {MODES.map((m) => {
                      const active = photoNameSettings.mode === m.value;
                      return (
                        <div key={m.value}>
                          <button
                            type="button"
                            onClick={() => setPhotoNameSettings(prev => ({ ...prev, mode: m.value }))}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                              active ? "bg-orange-50 dark:bg-orange-950/30" : "hover:bg-zinc-50 dark:hover:bg-zinc-800"
                            }`}
                            role="radio"
                            aria-checked={active}
                          >
                            <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              active ? "border-primary" : "border-zinc-300 dark:border-zinc-600"
                            }`}>
                              {active && <span className="w-2 h-2 rounded-full bg-primary" />}
                            </span>
                            <span className={`text-[13px] font-semibold ${active ? "text-primary" : "text-zinc-700 dark:text-zinc-200"}`}>
                              {m.label}
                            </span>
                            <span className="ml-auto text-[11px] text-zinc-400 truncate max-w-[45%] text-right">{m.example}</span>
                          </button>
                          {m.value === "custom" && active && (
                            <div className="px-3 pb-2.5 pt-0.5 bg-orange-50 dark:bg-orange-950/30">
                              <input
                                type="text"
                                autoFocus
                                value={photoNameSettings.custom || ""}
                                maxLength={40}
                                onChange={(e) => setPhotoNameSettings(prev => ({ ...prev, custom: e.target.value }))}
                                placeholder={t("photoNameSettings.customPlaceholder")}
                                className="w-full px-2.5 py-1.5 border border-primary/40 rounded-md text-[13px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* RIGHT — date toggle + live preview */}
                  <div>
                    <Switch
                      label={t("photoNameSettings.includeDate")}
                      checked={photoNameSettings.includeDate !== false}
                      onChange={(e) => { const checked = e.currentTarget.checked; setPhotoNameSettings(prev => ({ ...prev, includeDate: checked })); }}
                    />

                    {/* Live preview — serial + extension are fixed, shown only */}
                    <p className="mt-3.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t("photoNameSettings.preview")}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <div className="inline-flex items-center gap-0.5 flex-wrap rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm">
                      <span className="font-semibold text-zinc-800 dark:text-zinc-100">{prefix}</span>
                      {photoNameSettings.includeDate !== false && (
                        <span className="text-zinc-500 dark:text-zinc-400"> - {today}</span>
                      )}
                      <span className="text-zinc-400"> - </span>
                      <span
                        className="mx-0.5 px-1.5 py-0.5 rounded bg-zinc-200 dark:bg-zinc-700 text-[11.5px] font-bold text-zinc-500 dark:text-zinc-300 select-none cursor-default"
                        title={t("photoNameSettings.serialHint")}
                      >
                        0001
                      </span>
                      <span className="text-zinc-400">.jpg</span>
                    </div>
                    {/* Serial note sits beside the preview box (wraps below it when narrow). */}
                    <p className="flex-1 min-w-[160px] text-[11px] text-zinc-400">{t("photoNameSettings.serialHint")}</p>
                    </div>
                    {/* How the serial behaves with the CURRENT date toggle (see nextPhotoSerial). */}
                    <p className="mt-1.5 text-[11px] font-medium text-orange-600 dark:text-orange-400">
                      {t(photoNameSettings.includeDate !== false ? "photoNameSettings.serialDailyReset" : "photoNameSettings.serialContinuous")}
                    </p>
                  </div>
                  </div>{/* end two-column grid */}
                </div>
              );
            })()}
          </div>

          {/* Save all settings button */}
          <div className="order-6 flex justify-end">
            <SaveButton
              dirty={photoEditDirty} saving={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const updated = await authApi.updateProfile({ defaultPrompts });
                  updateProfile(updated);
                  await updateSettings({ printSizes, defaultImageGap, photoNameSettings, showSuitColorOptions, enabledDressStyles: enabledDressStyles || [], enabledDressColors: enabledDressColors || [], applyAiTemplateWatermark, qualityHdEnabled, qualityUltraEnabled });
                  notifications.show({ title: t("common.success"), message: t("profile.profileUpdated"), color: "green" });
                } catch (err) {
                  notifications.show({ title: t("common.error"), message: err.message, color: "red" });
                } finally { setLoading(false); }
              }}
            />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── General tab ──                                                 */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "general" && user?.role !== "admin" && (
        <div className="flex flex-col gap-3.5">

          {/* Module settings — one big tile per module: icon on top, title only below. */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7">
            {[
              // Highlighted: the one tile that makes money for the platform — owners only (same rule as the Buy Credit tab).
              { to: "/settings?tab=buycredit",         title: t("payments.buyTitle"),      icon: IconCoins,         hue: 32, highlight: true, ownerOnly: true },
              { to: "/settings?tab=photoeditsettings", title: t("settings.tileAiPhoto"),   glyph: "/module-icons/ai-tools.svg", hue: 288 },
              { onClick: "invoiceSettings",     title: t("settings.tileInvoiceSettings"), icon: IconFileInvoice, hue: 265 },
              { to: "/tools/form-auto-fillup/customers/new", title: t("settings.tileAddCustomer"), icon: IconUserPlus, hue: 330 },
              { to: "/settings?tab=incomeexpense",     title: t("settings.tileIncome"),    icon: IconSettings, hue: 232 },
              { to: "/officespaces",                   title: t("settings.tileShop"),      icon: IconBuildingStore, hue: 350 },
              { onClick: "jobPoster",           title: t("settings.tileJobPoster"), glyph: "/module-icons/job.svg", hue: 160 },
              { to: "/tools",                          title: t("settings.linkTools"),     icon: IconTools, hue: 42 },
              { to: "/dorkari-links",                  title: t("settings.linkDorkariLinks"), icon: IconLink, hue: 20 },
              { to: "/profile",                        title: t("settings.linkProfile"),   icon: IconUserCircle, hue: 245 },
            ]
              // "Useful Links" is a Bangladesh-only feature — hidden for users whose country is set elsewhere (unset → shown).
              .filter((item) => item.to !== "/dorkari-links" || !user?.country || user.country === "BD")
              .filter((item) => !item.ownerOnly || user?.role === "OWNER")
              .map(({ to, onClick, title, icon: Icon, glyph, hue, highlight }) => {
              const Tile = to ? Link : "button";
              const tileProps = to ? { to } : { type: "button", onClick: () => (onClick === "invoiceSettings" ? setInvoiceModalOpen(true) : onClick === "jobPoster" ? setJobPosterOpen(true) : null) };
              return (
              <Tile
                key={`${to || onClick}-${title}`}
                {...tileProps}
                className="group flex flex-col items-center gap-2.5 rounded-xl border border-transparent px-2 py-3 text-center transition-all hover:bg-white dark:hover:bg-zinc-800"
              >
                {/* Same coloured squircle tile as the dashboard shortcuts (utils/tileStyle). Buy Credit is the brand orange with a glossy cap + a shine that sweeps across (.tile-shine, global.scss). */}
                <span
                  style={{ ...squircleStyle, backgroundImage: linkGradient(hue) }}
                  className="relative flex h-20 w-20 items-center justify-center text-white drop-shadow-lg transition-transform duration-150 group-hover:scale-[1.03] sm:h-[88px] sm:w-[88px] lg:h-24 lg:w-24 xl:h-[104px] xl:w-[104px]"
                >
                  {/* the glyph is always half the tile, so it scales with the tile size above */}
                  {glyph ? <span className="relative block h-1/2 w-1/2"><ModuleGlyph src={glyph} size="100%" /></span> : <Icon stroke={1.8} className="relative h-1/2 w-1/2" />}
                  {highlight && (
                    <>
                      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/50 to-white/0" />
                      <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                        <span className="tile-shine absolute inset-y-0 -left-1/2 w-1/3 bg-gradient-to-r from-white/0 via-white/60 to-white/0" />
                      </span>
                    </>
                  )}
                </span>
                <span className={`relative text-sm font-semibold leading-snug ${highlight ? "text-primary" : "text-zinc-800 dark:text-zinc-100"}`}>{title}</span>
              </Tile>
              );
            })}
          </div>

          {/* "জব পোস্টার ডিজাইন" tile → the same full-screen poster editor as the dashboard / Jobs page */}
          <BiggaponModal open={jobPosterOpen} onClose={() => setJobPosterOpen(false)} />

          {/* "ইনভয়েস সেটিংস" tile → the same full-screen invoice customise window as on the invoice page */}
          <InvoiceSettings
            open={invoiceModalOpen}
            onOpenChange={setInvoiceModalOpen}
            shop={{
              shopName: selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName,
              businessAddress: selectedOfficeSpace?.businessProfile?.businessAddress || user?.businessAddress,
              phone: selectedOfficeSpace?.businessProfile?.shopPhone || user?.phone,
              email: selectedOfficeSpace?.businessProfile?.shopEmail || "",
              shopLogo: selectedOfficeSpace?.businessProfile?.shopLogo || "",
              facebookUrl: selectedOfficeSpace?.businessProfile?.facebookUrl || "",
              proprietorName: user?.name || "",
            }}
          />

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Stamp preview + mobile app: two cards sharing one row (stacked below lg) */}
          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
            {/* Stamp preview */}
            <ToggleCard
              title={t("profile.stampPreviewEnabled") || "Enable Stamp Preview"}
              description={t("profile.stampPreviewEnabledDesc") || "Show the stamp overlay when previewing stamp documents."}
              checked={user?.settings?.stampPreviewEnabled !== false}
              onCheckedChange={async (val) => {
                try {
                  await updateSettings({ stampPreviewEnabled: val });
                  notifications.show({ title: t("common.success"), message: t("profile.settingsSaved") || "Settings saved", color: "green" });
                } catch (err) { notifications.show({ title: t("common.error"), message: err.message, color: "red" }); }
              }}
            />

            {/* Mobile app — Google Play (same link and labels as the mobile menu drawer) */}
            <a
              href={MOBILE_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm px-4 sm:px-[22px] py-[18px] transition-colors hover:border-primary/40 sm:flex-row sm:items-center"
            >
              <span className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-black dark:border dark:border-zinc-600">
                  <img src="/images/google-play.svg" alt="" className="h-6 w-6" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">{t("navigation.mobileApp")}</span>
                  <span className="block text-xs text-zinc-500 dark:text-zinc-400">{t("settings.mobileAppDesc")}</span>
                </span>
              </span>
              <span className="inline-flex flex-none items-center justify-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white transition-colors group-hover:bg-zinc-800 dark:border dark:border-zinc-600">
                <IconDownload size={16} stroke={2.2} />
                {t("navigation.downloadApp")}
              </span>
            </a>
          </div>

        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── Income/Expense settings tab ──                                 */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "incomeexpense" && user?.role !== "admin" && (
        <div className="flex flex-col gap-3.5">
          {/* Name + price service list (modules, predefined, custom) — compact, at top */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3 mb-2.5">
              <div className="min-w-0">
                <div className="text-[15px] font-semibold text-zinc-800 dark:text-zinc-100">{t('incomeExpense.suggestionsTitle') || 'আপনার দেয়া সার্ভিসগুলোর নামের তালিকা'}</div>
                <div className="text-[12px] text-zinc-500 dark:text-zinc-400 mt-0.5">{t('incomeExpense.suggestionsDescShort') || 'সার্ভিসের নাম ও মূল্য। “অটো” মডিউলের কাজ শেষে ঐ মূল্যেই পপআপ খুলবে।'}</div>
              </div>
              {/* Income / Expense toggle — each type keeps its own list */}
              <div className="inline-flex flex-none rounded-lg p-0.5 bg-zinc-100 dark:bg-zinc-800">
                <button type="button" onClick={() => setSuggestionType('income')}
                  className={`px-3 py-1 rounded-md text-[12.5px] font-semibold transition-colors ${suggestionType === 'income' ? 'bg-emerald-500 text-white shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                  {t('incomeExpense.income')}
                </button>
                <button type="button" onClick={() => setSuggestionType('expense')}
                  className={`px-3 py-1 rounded-md text-[12.5px] font-semibold transition-colors ${suggestionType === 'expense' ? 'bg-red-500 text-white shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                  {t('incomeExpense.expense')}
                </button>
              </div>
            </div>

            {/* Column captions */}
            <div className="flex items-center gap-2 mb-1 px-0.5">
              <div className="flex-1 text-[11px] font-semibold text-zinc-400 dark:text-zinc-500">{t('incomeExpense.serviceNameCol') || 'সার্ভিসের নাম'}</div>
              <div className="w-[92px] text-[11px] font-semibold text-zinc-400 dark:text-zinc-500">{t('incomeExpense.priceCol') || 'মূল্য (৳)'}</div>
              <div className="w-8" />
            </div>

            {/* Service rows — modules (fixed name, price only) then user entries */}
            <div className="flex flex-col gap-1.5 mb-2">
              {activeSuggestions.map((s, i) => (
                // Key must be stable while typing — including s.name here would change the
                // key on every keystroke, remount the input and drop focus. Position is stable.
                <div key={s.category || `c-${i}`} className="flex items-center gap-2">
                  {s.category ? (
                    renderNameCell(s)
                  ) : suggestionType === 'income' ? (
                    // Income names live on the active shop's business profile — renaming here edits it there.
                    renderNameCell(s)
                  ) : (
                    <input type="text" value={s.name}
                      onChange={(e) => updateSuggestion(i, { name: e.target.value })}
                      placeholder={t('incomeExpense.serviceNamePlaceholder') || 'সার্ভিসের নাম'}
                      className="flex-1 min-w-0 h-9 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-[13px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  )}
                  <input type="text" inputMode="decimal"
                    value={s.price == null ? '' : String(s.price)}
                    onChange={(e) => updateSuggestion(i, { price: parsePrice(sanitizeAmountInput(e.target.value)) })}
                    placeholder={t('incomeExpense.noPricePlaceholder') || 'মূল্য নেই'}
                    className="w-[92px] flex-none h-9 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-[13px] font-semibold bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  {s.category ? (
                    // Auto modules can't be deleted.
                    <div className="w-8 flex-none" />
                  ) : (
                    // Income rows are shop services → delete from the business profile;
                    // expense rows are local → remove from the list.
                    <button type="button"
                      onClick={() => (suggestionType === 'income' ? removeServiceFromProfile(s.name) : removeSuggestion(i))}
                      disabled={suggestionType === 'income' && serviceSaving}
                      title={t('common.delete') || 'মুছুন'}
                      className="w-8 h-8 flex-none flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-50">
                      <IconTrash size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Expense entries are managed here (name + optional price). Income service
                NAMES come from the active shop's profile, so no add row for income. */}
            {suggestionType === 'expense' && (
              <div className="flex gap-2">
                <input type="text" value={suggestionInput}
                  onChange={(e) => setSuggestionInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuggestion(); } }}
                  placeholder={t('incomeExpense.addSuggestionPlaceholder') || 'নতুন সার্ভিসের নাম'}
                  className="flex-1 min-w-0 h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-[13px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <input type="text" inputMode="decimal" value={suggestionPriceInput}
                  onChange={(e) => setSuggestionPriceInput(sanitizeAmountInput(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuggestion(); } }}
                  placeholder={t('incomeExpense.priceOptional') || 'মূল্য'}
                  className="w-[92px] flex-none h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-[13px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <button type="button" onClick={addSuggestion}
                  className="w-10 h-10 flex items-center justify-center bg-primary hover:bg-primary/90 text-white rounded-lg flex-shrink-0">
                  <IconPlus size={18} />
                </button>
              </div>
            )}

            {/* Income service names live on the shop profile. Add one right here (preset
                suggestions appear on focus); it saves straight into the business profile. */}
            {suggestionType === 'income' && (
              <div className="space-y-1.5">
                <div className="relative flex gap-2">
                  <div className="relative flex-1 min-w-0">
                    <input type="text" value={serviceAddInput}
                      onChange={(e) => { setServiceAddInput(e.target.value); setServiceAddOpen(true); }}
                      onFocus={() => setServiceAddOpen(true)}
                      onBlur={() => setTimeout(() => setServiceAddOpen(false), 150)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addServiceToProfile(serviceAddInput); } }}
                      disabled={!selectedOfficeSpace?.id || serviceSaving}
                      placeholder={t('incomeExpense.addServicePlaceholder') || 'নতুন সার্ভিস যোগ করুন...'}
                      className="w-full h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-[13px] bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                    />
                    {serviceAddOpen && presetServiceSuggestions.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg">
                        {presetServiceSuggestions.map((nm) => (
                          <button key={nm} type="button"
                            onMouseDown={(e) => { e.preventDefault(); addServiceToProfile(nm); }}
                            className="block w-full text-left px-3 py-2 text-[13px] text-zinc-700 dark:text-zinc-200 hover:bg-primary/5 hover:text-primary transition-colors">
                            {nm}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => addServiceToProfile(serviceAddInput)}
                    disabled={!selectedOfficeSpace?.id || serviceSaving || !serviceAddInput.trim()}
                    title={t('incomeExpense.addService') || 'সার্ভিস যোগ করুন'}
                    className="w-10 h-10 flex items-center justify-center bg-primary hover:bg-primary/90 text-white rounded-lg flex-shrink-0 disabled:opacity-50">
                    {serviceSaving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <IconPlus size={18} />}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-400 dark:text-zinc-500">
                  {t('incomeExpense.servicesSaveToShopHint') || 'যোগ করা সার্ভিস দোকানের প্রোফাইলে সেভ হবে — এখানে শুধু মূল্য দিন।'}
                  {selectedOfficeSpace?.id && (
                    <Link to={`/officespaces/${selectedOfficeSpace.id}/settings`} className="ml-1 text-primary hover:underline font-medium">
                      {t('incomeExpense.manageServices') || 'সার্ভিস ম্যানেজ করুন'}
                    </Link>
                  )}
                </p>
              </div>
            )}

            {/* Poster (income list only) + Save */}
            <div className="mt-3 flex items-center justify-end gap-2">
            {/* The poster editor opens with the list as it is on screen right now (saved or not). */}
            {suggestionType === 'income' && <RateChartButton services={incomeSuggestions} className="mr-auto" />}
            <SaveButton dirty={suggestionsDirty} saving={suggestionsSaving} onClick={() => persistSuggestions()} />
            </div>
          </div>

          {/* Entry preferences — the two toggles + shortcut hint, one compact card */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm divide-y divide-zinc-100 dark:divide-zinc-800">
            {/* Show/hide button */}
            <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-100">{t('incomeExpense.showBtn')}</div>
                <div className="text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{t('incomeExpense.showBtnDesc')}</div>
              </div>
              <UiSwitch
                checked={user?.settings?.showIncomeExpenseBtn !== false}
                onCheckedChange={async (val) => {
                  try { await updateSettings({ showIncomeExpenseBtn: val }); notifications.show({ title: t("common.success"), message: t("profile.settingsSaved"), color: "green" }); }
                  catch (err) { notifications.show({ title: t("common.error"), message: err.message, color: "red" }); }
                }}
                className={`shrink-0 ${switchCls}`}
              />
            </div>

            {/* Auto-appear toggle */}
            <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-100">{t('incomeExpense.autoAppear')}</div>
                <div className="text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{t('incomeExpense.autoAppearDesc')}</div>
              </div>
              <UiSwitch
                checked={user?.settings?.autoAppearIncomeExpense === true}
                onCheckedChange={async (val) => {
                  try { await updateSettings({ autoAppearIncomeExpense: val }); notifications.show({ title: t("common.success"), message: t("profile.settingsSaved"), color: "green" }); }
                  catch (err) { notifications.show({ title: t("common.error"), message: err.message, color: "red" }); }
                }}
                className={`shrink-0 ${switchCls}`}
              />
            </div>

            {/* Quick action — open the আয়/ব্যয় add popup */}
            <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-100">{t('incomeExpense.quickAddTitle') || 'দ্রুত আয়/ব্যয় যোগ করুন'}</div>
                <div className="text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{t('incomeExpense.quickAddDesc') || 'যেকোনো সময় একটি আয় বা ব্যয় এন্ট্রি যোগ করতে পপআপ খুলুন।'}</div>
              </div>
              <button
                type="button"
                onClick={() => openIncomeExpenseModal()}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white px-3.5 h-[38px] text-[13px] font-semibold transition-colors"
              >
                <IconPlus size={16} /> {t('incomeExpense.addEntry') || 'যোগ করুন'}
              </button>
            </div>

            {/* Keyboard shortcut hint — compact footer strip */}
            <div className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-blue-50/60 dark:bg-blue-900/10 rounded-b-xl">
              <IconKeyboard size={16} className="flex-none text-blue-600 dark:text-blue-400" stroke={1.8} />
              <p className="text-[12px] leading-relaxed text-blue-700 dark:text-blue-300">
                {t('incomeExpense.shortcutHintPrefix')}{' '}
                <kbd className="rounded-md border border-blue-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-zinc-800 dark:text-blue-300">Ctrl</kbd>
                {' / '}
                <kbd className="rounded-md border border-blue-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-zinc-800 dark:text-blue-300">Cmd</kbd>
                {' + '}
                <kbd className="rounded-md border border-blue-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:border-blue-800 dark:bg-zinc-800 dark:text-blue-300">I</kbd>{' '}
                {t('incomeExpense.shortcutHintSuffix')}
              </p>
            </div>
          </div>

          {/* Invoice template chooser (with live previews) — moved to the bottom */}
          <InvoiceTemplatePicker />

        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── Buy credit tab ──                                              */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* One tab for everyone; `buyGateway` (derived above from region + activePaymentGateway) */}
      {/* decides which gateway/packages/currency show. BD → Moneybag/SSLCommerz (BDT),         */}
      {/* non-BD → FastSpring (USD). A non-BD user can't reach the BDT gateway via the URL.      */}
      {activeTab === "buycredit" && user?.role === "OWNER" && (
        showBuyCreditTab ? (
          <>
            <LowCreditBanner credit={user?.credit ?? 0} className="mb-4" showBuyButton={false} />
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-3 sm:p-6">
              <BuyCreditTab gateway={buyGateway} />
            </div>
          </>
        ) : (
          <PaymentMaintenanceNotice t={t} />
        )
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── Credits tab ──                                                 */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "credits" && (user?.role === "OWNER" || user?.role === "admin") && (
        <div className="flex flex-col gap-4">
          {/* Low / advance credit warning */}
          <LowCreditBanner credit={user?.credit ?? 0} className="" />
          {/* Migration banner intentionally disabled — the optional credit-migration nudge is no
             longer shown. Previously it rendered <CreditMigrationBanner /> while un-migrated
             profile credit remained (guarded by `showMigration`). Kept here for easy re-enable:
          {showMigration ? (
          <CreditMigrationBanner />
          ) : (
          */}
          {/* Per-action cost breakdown — now always shown for owner/admin */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-[22px]">
            <div className="text-[15px] text-zinc-700 dark:text-zinc-300 mb-4">
              <span className="font-bold text-zinc-800 dark:text-zinc-100">{t('profile.creditWhatTitle')}</span> {t('profile.creditWhatDesc')}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { iconSrc: "/module-icons/ai-tools.svg", iconBg: "bg-violet-100 dark:bg-violet-900/50", iconColor: "text-violet-600 dark:text-violet-400", labelPath: "navigation.generateImageAI", costKey: "IMAGE" },
              // Studio Editor bills the IMAGE module at the AI editor's HD / Ultra HD tier prices
              // (it has no Normal tier), so the card lists both tier rates instead of one cost.
              { iconSrc: "/module-icons/studio-editor.svg", iconBg: "bg-teal-100 dark:bg-teal-900/50", iconColor: "text-teal-600 dark:text-teal-400", labelPath: "navigation.studioEditor", imageTiers: ["hd", "ultra"] },
              { iconSrc: "/module-icons/manual-photo-edit.svg", iconBg: "bg-emerald-100 dark:bg-emerald-900/50", iconColor: "text-emerald-600 dark:text-emerald-400", labelPath: "navigation.generateImageManual", costKey: "IMAGE_MANUAL" },
              { iconSrc: "/module-icons/bulk-photo-editing.svg", iconBg: "bg-amber-100 dark:bg-amber-900/50", iconColor: "text-amber-600 dark:text-amber-400", labelPath: "navigation.bulkPhotoEdit", costKey: "BULK_PHOTO" },
              { iconSrc: "/module-icons/ai-templates.svg", iconBg: "bg-purple-100 dark:bg-purple-900/50", iconColor: "text-purple-600 dark:text-purple-400", labelPath: "navigation.aiTemplates", costKey: "AI_TEMPLATE" },
              { iconSrc: "/module-icons/print-media.svg", iconBg: "bg-sky-100 dark:bg-sky-900/50", iconColor: "text-sky-600 dark:text-sky-400", labelPath: "navigation.printMedia", costKey: "PRINT" },
              { iconSrc: "/module-icons/ai-documents.svg", iconBg: "bg-cyan-100 dark:bg-cyan-900/50", iconColor: "text-cyan-600 dark:text-cyan-400", labelPath: "profile.creditModule_AI_DOCUMENT", costKey: "AI_DOCUMENT" },
              { iconSrc: "/module-icons/document-set.svg", iconBg: "bg-blue-100 dark:bg-blue-900/50", iconColor: "text-blue-600 dark:text-blue-400", labelPath: "profile.creditModule_DOCUMENT", costKey: "DOCUMENT" },
              { iconSrc: "/questions-create.svg", iconBg: "bg-rose-100 dark:bg-rose-900/50", iconColor: "text-rose-600 dark:text-rose-400", labelPath: "profile.creditModule_QUESTION_COMBINED", subItems: [
                { costKey: "QUESTION_CREATE" },
                { costKey: "QUESTION_EXPORT" },
              ] },
              // Real cost from the API (creditCost_FORM_FILL / walletCreditCost_FORM_FILL):
              // the AI CV-parse bills FORM_FILL per successful parse, so the tile shows the
              // live value (0 = free pill, otherwise the credit cost) like every paid tile.
              { iconEl: <IconForms size={18} />, iconBg: "bg-indigo-100 dark:bg-indigo-900/50", iconColor: "text-indigo-600 dark:text-indigo-400", labelPath: "navigation.formAutoFillup", costKey: "FORM_FILL" },
              // Extension autofill (delivering a customer record to fill a web form): its own
              // billable module (creditCost_EXTENSION_FILL / walletCreditCost_EXTENSION_FILL),
              // live value from the API — 0 shows the free pill.
              { iconEl: <IconPuzzle size={18} />, iconBg: "bg-indigo-100 dark:bg-indigo-900/50", iconColor: "text-indigo-600 dark:text-indigo-400", labelPath: "profile.creditModule_EXTENSION_FILL", costKey: "EXTENSION_FILL" },
              { iconSrc: "/module-icons/easy-tools.svg", iconBg: "bg-fuchsia-100 dark:bg-fuchsia-900/50", iconColor: "text-fuchsia-600 dark:text-fuchsia-400", labelPath: "profile.creditToolsLabel", badgeText: t("profile.creditFree"), labelClass: "!font-bold text-primary" },
              // The handful of tools that call the AI on the server — the rest of
              // the tools run in the browser and stay free (the card above).
              { iconEl: <IconSparkles size={18} />, iconBg: "bg-amber-100 dark:bg-amber-900/50", iconColor: "text-amber-600 dark:text-amber-400", labelPath: "profile.creditModule_AI_TOOL", costKey: "AI_TOOL" },
              // Always-free features — static cards so users see they cost nothing.
              { iconEl: <IconCurrencyTaka size={18} />, iconBg: "bg-lime-100 dark:bg-lime-900/50", iconColor: "text-lime-600 dark:text-lime-400", labelPath: "profile.creditFeature_incomeDena", badgeText: t("profile.creditFree") },
              { iconEl: <IconFileInvoice size={18} />, iconBg: "bg-orange-100 dark:bg-orange-900/50", iconColor: "text-orange-600 dark:text-orange-400", labelPath: "incomeExpense.invoiceTab", badgeText: t("profile.creditFree") },
              { iconEl: <IconUsers size={18} />, iconBg: "bg-pink-100 dark:bg-pink-900/50", iconColor: "text-pink-600 dark:text-pink-400", labelPath: "profile.creditFeature_customerManage", badgeText: t("profile.creditFree") },
              { iconSrc: "/module-icons/job.svg", iconBg: "bg-teal-100 dark:bg-teal-900/50", iconColor: "text-teal-600 dark:text-teal-400", labelPath: "profile.creditFeature_jobsLinks", badgeText: t("profile.creditFree") },
            ].map(item => {
              // Combined tile — question create + export share one card: normal card
              // layout with ONE label line; the top-right shows a single cost when both
              // actions cost the same, or "create / export" costs when they differ.
              if (item.subItems) {
                const rates = item.subItems.map((sub) => ({
                  old: creditCosts?.legacy?.[sub.costKey] ?? CREDIT_COSTS[sub.costKey],
                  nw: creditCosts?.wallet?.[sub.costKey] ?? WALLET_CREDIT_COSTS[sub.costKey],
                }));
                const allSame = rates.every((r) => r.old === rates[0].old && r.nw === rates[0].nw);
                const freePill = (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-emerald-600 dark:text-emerald-400 font-bold text-[11px] leading-none">
                    {t("profile.creditFree")}
                  </span>
                );
                const costEl = (r, k) => (
                  <span key={k} className="inline-flex items-center leading-none">
                    {showOldCost && showNewCost ? (
                      <span className="inline-flex items-center gap-1 leading-none">
                        <span className="text-zinc-400 dark:text-zinc-500 font-semibold text-[14px]">{toBn(r.old)}</span>
                        <IconArrowRight size={11} className="text-emerald-500 flex-shrink-0" />
                        {r.nw === 0 ? freePill : <span className="text-primary font-bold text-[16px]">{toBn(r.nw)}</span>}
                      </span>
                    ) : (showOldCost ? r.old : r.nw) === 0 ? freePill : (
                      <span className="inline-flex items-baseline gap-1 text-primary font-bold text-[18px] leading-none">
                        {toBn(showOldCost ? r.old : r.nw)}
                        <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500">{t("profile.creditCostUnit")}</span>
                      </span>
                    )}
                  </span>
                );
                return (
                  <div key="QUESTION_COMBINED" className="border border-zinc-200 dark:border-zinc-700 rounded-[14px] p-[15px] bg-white dark:bg-zinc-900 flex flex-col gap-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`w-8 h-8 rounded-[9px] ${item.iconBg} ${item.iconColor} flex items-center justify-center`}>
                        <ModuleGlyph src={item.iconSrc} />
                      </span>
                      {creditLoading ? <Skeleton height={16} width={44} /> : (
                        <span className="inline-flex items-center gap-1.5">
                          {allSame ? costEl(rates[0], "one") : (
                            <>
                              {costEl(rates[0], "create")}
                              <span className="text-zinc-300 dark:text-zinc-600 text-[12px]">/</span>
                              {costEl(rates[1], "export")}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 leading-snug">{t(item.labelPath)}</p>
                    </div>
                  </div>
                );
              }
              // Tier-priced tile (Studio Editor) — one row per quality tier, legacy → wallet when both pools apply.
              if (item.imageTiers) {
                const rates = creditCosts?.imageTierRates;
                return (
                  <div key={item.labelPath} className="border border-zinc-200 dark:border-zinc-700 rounded-[14px] p-[15px] bg-white dark:bg-zinc-900 flex flex-col gap-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`w-8 h-8 rounded-[9px] ${item.iconBg} ${item.iconColor} flex items-center justify-center`}>
                        <ModuleGlyph src={item.iconSrc} />
                      </span>
                      <p className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 leading-snug text-right">{t(item.labelPath)}</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {item.imageTiers.map((tier) => {
                        const r = rates?.[tier];
                        return (
                          <div key={tier} className="flex items-center justify-between gap-2">
                            <span className="text-[12px] text-zinc-500 dark:text-zinc-400">
                              {t(`imageGeneration.quality.${tier}`)}
                              {/* English tier name alongside (skipped when the UI is already English) */}
                              {i18n.language !== "en" && <span className="text-[11px] text-zinc-400 dark:text-zinc-500"> ({t(`imageGeneration.quality.${tier}`, { lng: "en" })})</span>}
                            </span>
                            {creditLoading || !r ? <Skeleton height={14} width={40} /> : (
                              <span className="inline-flex items-baseline gap-1 leading-none">
                                {showOldCost && showNewCost && (
                                  <>
                                    <span className="text-zinc-400 dark:text-zinc-500 font-semibold text-[13px]">{toBn(r.old)}</span>
                                    <IconArrowRight size={11} className="text-emerald-500 flex-shrink-0 self-center" />
                                  </>
                                )}
                                <span className="text-primary font-bold text-[16px]">{toBn(showOldCost && !showNewCost ? r.old : r.new)}</span>
                                <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500">{t("profile.creditCostUnit")}</span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Old (legacy) vs new (wallet) per-action cost — API value first, constant fallback.
              const oldCost = creditCosts?.legacy?.[item.costKey] ?? CREDIT_COSTS[item.costKey];
              const newCost = creditCosts?.wallet?.[item.costKey] ?? WALLET_CREDIT_COSTS[item.costKey];
              // Free-module offer: does this module have a rule (can be free with a package),
              // and does THIS user already have an active grant? The cost stays visible either
              // way — a separate info icon (below) explains the free option / active grant.
              const moduleRules = (creditConfig?.moduleFreeOfferRules || []).filter((r) => r.module === item.costKey);
              const maxFreeMonths = moduleRules.length ? Math.max(...moduleRules.map((r) => r.months)) : null;
              const activeGrant = creditConfig?.freeModules?.[item.costKey];
              // AI editor only: HD / Ultra HD quality tiers cost extra — surface their
              // dynamic Setting-table rates in a tooltip, using the same pool logic as
              // the card price (both pools → legacy → wallet arrow, else the payer pool).
              const tierRates = item.costKey === "IMAGE" ? creditCosts?.imageTierRates : null;
              const fmtTierCost = (r) => (showOldCost && showNewCost
                ? `${toBn(r.old)} → ${toBn(r.new)}`
                : toBn(showOldCost ? r.old : r.new));
              const qualityTipLabel = tierRates?.hd && tierRates?.ultra
                ? `${t("imageGeneration.quality.hd")}: ${fmtTierCost(tierRates.hd)} ${t("profile.creditCostUnit")} • ${t("imageGeneration.quality.ultra")}: ${fmtTierCost(tierRates.ultra)} ${t("profile.creditCostUnit")}`
                : null;
              // A single new-cost pill (reused as the "new" side of the comparison too).
              const newCostPill = newCost === 0 ? (
                <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-emerald-600 dark:text-emerald-400 font-bold text-[11px] leading-none">
                  {t("profile.creditFree")}
                </span>
              ) : (
                <span className="inline-flex items-baseline gap-1 text-primary font-bold text-[22px] leading-none">
                  {toBn(newCost)}
                  <span className="text-[12px] font-medium text-zinc-400 dark:text-zinc-500">{t("profile.creditCostUnit")}</span>
                </span>
              );
              // Legacy rate — the "old" side of the comparison / old-only view.
              const oldCostText = (
                <span className="inline-flex items-baseline gap-1 text-zinc-400 dark:text-zinc-500 font-semibold text-[18px] leading-none">
                  {toBn(oldCost)}
                  <span className="text-[11px] font-medium">{t("profile.creditCostUnit")}</span>
                </span>
              );
              return (
              <div key={item.costKey || item.labelPath} className="border border-zinc-200 dark:border-zinc-700 rounded-[14px] p-[15px] bg-white dark:bg-zinc-900 flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className={`w-8 h-8 rounded-[9px] ${item.iconBg} ${item.iconColor} flex items-center justify-center`}>
                    {item.iconSrc ? <ModuleGlyph src={item.iconSrc} /> : item.iconEl}
                  </span>
                  {item.badgeText !== undefined ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-emerald-600 dark:text-emerald-400 font-bold text-[12px] leading-none">
                      {item.badgeText}
                    </span>
                  ) : creditLoading ? <Skeleton height={16} width={44} /> : (
                    // Both balances → labelled old→new comparison; only legacy → old cost; else → new cost.
                    showOldCost && showNewCost ? (
                      <span className="inline-flex items-center gap-1.5 leading-none">
                        <span className="flex flex-col items-end gap-1">
                          <span className="text-[9px] font-medium text-zinc-400 dark:text-zinc-500 whitespace-nowrap">{t("profile.creditOldLabel")}</span>
                          <span className="text-zinc-400 dark:text-zinc-500 font-semibold text-[18px] leading-none">{toBn(oldCost)}</span>
                        </span>
                        <IconArrowRight size={13} className="text-emerald-500 flex-shrink-0 self-end mb-0.5" />
                        <span className="flex flex-col items-start gap-1">
                          <span className="text-[9px] font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{t("profile.creditNewLabel")}</span>
                          {newCost === 0 ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-emerald-600 dark:text-emerald-400 font-bold text-[11px] leading-none">
                              {t("profile.creditFree")}
                            </span>
                          ) : (
                            <span className="text-primary font-bold text-[22px] leading-none">{toBn(newCost)}</span>
                          )}
                        </span>
                      </span>
                    ) : showOldCost ? oldCostText : newCostPill
                  )}
                </div>
                <div>
                  {/* labelClass overrides need ! (e.g. !font-bold) — global.scss forces p to weight 400 */}
                  <p className={`text-[13px] leading-snug inline-flex items-center gap-1 ${item.labelClass || "font-semibold text-zinc-800 dark:text-zinc-100"}`}>
                    {t(item.labelPath)}
                    {qualityTipLabel && (
                      <Tip label={qualityTipLabel}>
                        <IconInfoCircle size={14} className="cursor-help text-zinc-400 dark:text-zinc-500" />
                      </Tip>
                    )}
                    {(activeGrant || maxFreeMonths) && (
                      <Tip label={t('profile.freeModuleOfferTooltip', { duration: formatFreeDuration(maxFreeMonths || activeGrant?.months) })}>
                        <IconInfoCircle size={14} className={`cursor-help ${activeGrant ? 'text-emerald-500' : 'text-zinc-400 dark:text-zinc-500'}`} />
                      </Tip>
                    )}
                  </p>
                  {item.notePath && <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">{t(item.notePath)}</p>}
                </div>
              </div>
              );
            })}
            </div>
          </div>
          {/* )}  end of disabled migration ternary */}

          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
            {[
              { labelKey: "creditStatsToday", value: creditStats?.today ?? 0, color: "text-zinc-800 dark:text-zinc-100" },
              { labelKey: "creditStatsLast7Days", value: creditStats?.last7Days ?? 0, color: "text-primary" },
              { labelKey: "creditStatsThisMonth", value: creditStats?.thisMonth ?? 0, color: "text-primary" },
              { labelKey: "creditStatsAllTime", value: creditStats?.allTime ?? 0, color: "text-violet-600 dark:text-violet-400" },
            ].map(card => (
              <div key={card.labelKey} className="border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm px-5 py-[18px] bg-white dark:bg-zinc-900">
                {creditLoading ? <Skeleton height={50} /> : (
                  <>
                    <p className="text-[13px] text-zinc-500 dark:text-zinc-400">{t(`profile.${card.labelKey}`)}</p>
                    <p className={`text-[30px] font-bold tabular-nums mt-1.5 leading-none ${card.color}`}>{toBn(Math.abs(card.value))}</p>
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1.5">{t("profile.creditStatsSpent")}</p>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Transaction history */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="flex justify-between items-center px-[22px] py-4 border-b border-zinc-200 dark:border-zinc-700">
              <h4 className="text-base font-bold text-zinc-800 dark:text-zinc-100">{t("profile.creditHistory")}</h4>
              <span className="inline-flex items-center gap-[7px] px-3 py-[5px] rounded-full text-[12.5px] font-semibold text-primary bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
                <IconShoppingCart size={14} />
                {t("profile.creditBalance")}: {(() => {
                  const { showBoth, showLegacyOnly, legacy, wallet } = creditParts(user);
                  // Old credit comes from the PROFILE field (legacy) and new credit from the
                  // WALLET balance — always kept separate. Never fall back to creditData.balance
                  // for the new part: that is the COMBINED total and would double-count the old.
                  return showBoth
                    ? `${t("profile.creditOldLabel")} ${toBn(legacy)} • ${t("profile.creditNewLabel")} ${toBn(wallet)}`
                    : showLegacyOnly
                    ? `${t("profile.creditNewLabel")} ${toBn(legacy)}`
                    : toBn(creditData?.balance ?? wallet);
                })()}
              </span>
            </div>
            {creditLoading ? (
              <div className="flex flex-col divide-y divide-zinc-100 dark:divide-zinc-800">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3"><Skeleton height={32} width={32} /><div className="flex-1"><Skeleton height={12} width="60%" /></div><Skeleton height={12} width={40} /></div>
                ))}
              </div>
            ) : !creditData?.transactions?.length ? (
              <p className="text-center text-zinc-400 py-8 text-sm">{t("profile.noCreditHistory")}</p>
            ) : (
              <>
                {isMobile ? (
                  <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {creditData.transactions.map((tx) => {
                      const isSettlement = tx.metadata?.type === "advance_settlement";
                      const isRecharge = !isSettlement && tx.balanceAfter > tx.balanceBefore;
                      const moduleColor = isSettlement ? "yellow" : isRecharge ? "green" : tx.module === "IMAGE" ? "violet" : tx.module === "IMAGE_MANUAL" ? "grape" : tx.module === "DOCUMENT" ? "blue" : "orange";
                      const iconBgMap = { green: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600", violet: "bg-violet-100 dark:bg-violet-900/30 text-violet-600", grape: "bg-purple-100 dark:bg-purple-900/30 text-purple-600", blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600", orange: "bg-orange-100 dark:bg-orange-900/30 text-orange-600", yellow: "bg-amber-100 dark:bg-amber-900/30 text-amber-600" };
                      return (
                        <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${iconBgMap[moduleColor] || iconBgMap.orange}`}>
                            {isSettlement ? <IconCreditCard size={16} /> : isRecharge ? <IconCoins size={16} /> : tx.module === "IMAGE" || tx.module === "IMAGE_MANUAL" ? <IconPhoto size={16} /> : tx.module === "DOCUMENT" || tx.module === "FORM_FILL" || tx.module === "EXTENSION_FILL" ? <IconFileDescription size={16} /> : tx.module === "PRINT" ? <IconPrinter size={16} /> : <IconCreditCard size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Badge color={moduleColor} size="sm">{isSettlement ? t("profile.creditAdvanceSettlement") : isRecharge ? t("profile.creditModule_RECHARGE") : t(creditModuleKey(tx))}</Badge>
                            <p className="text-[11px] text-zinc-400 mt-0.5">{new Date(tx.createdAt).toLocaleDateString(isBn ? "bn-BD" : undefined, { day: "numeric", month: "short", year: "numeric" })}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {isSettlement ? <span className="text-sm font-bold text-red-500">-{toBn(tx.cost)}</span> : isRecharge ? <span className="text-sm font-bold text-emerald-600">+{toBn(tx.balanceAfter - tx.balanceBefore)}</span> : <span className="text-sm font-bold text-red-500">{toBn(tx.cost)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-sm border-collapse">
                      <thead>
                        <tr className="text-left">
                          {[t("profile.creditModule"), t("profile.creditDoneBy"), t("profile.creditOfficeSpace"), t("profile.creditCost"), t("profile.creditDate")].map(h => (
                            <th key={h} className="text-left py-3 px-4 first:pl-[22px] last:pr-[22px] text-[12px] font-semibold text-zinc-500 dark:text-zinc-400">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {creditData.transactions.map((tx) => {
                          // Informational row: part of a recharge that settled prior advance (debt).
                          const isSettlement = tx.metadata?.type === "advance_settlement";
                          const isRecharge = !isSettlement && tx.balanceAfter > tx.balanceBefore;
                          const badgeColor = isSettlement ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                            : isRecharge ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : tx.module === "IMAGE" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                            : tx.module === "IMAGE_MANUAL" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                            : tx.module === "DOCUMENT" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                            : "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300";
                          return (
                          <tr key={tx.id} className="border-t border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                            <td className="py-[13px] px-4 first:pl-[22px]">
                              <span className={`inline-flex items-center rounded-[7px] px-[11px] py-[3px] text-[12px] font-semibold ${badgeColor}`}>
                                {isSettlement ? t("profile.creditAdvanceSettlement") : isRecharge ? t("profile.creditModule_RECHARGE") : t(creditModuleKey(tx))}
                              </span>
                            </td>
                            <td className="py-[13px] px-4 text-[13.5px] text-zinc-700 dark:text-zinc-300">{tx.doneBy}</td>
                            <td className="py-[13px] px-4 text-[13.5px] text-zinc-500 dark:text-zinc-400">{tx.officeSpace ?? "—"}</td>
                            <td className="py-[13px] px-4 text-[13.5px] font-bold">
                              {isSettlement
                                ? <span className="text-red-500">-{toBn(tx.cost)}</span>
                                : isRecharge
                                ? <span className="text-emerald-600">+{toBn(tx.balanceAfter - tx.balanceBefore)}</span>
                                : <span className="text-red-500">{toBn(tx.cost)}</span>
                              }
                            </td>
                            <td className="py-[13px] px-4 last:pr-[22px] whitespace-nowrap text-[13px] text-zinc-500 dark:text-zinc-400">{new Date(tx.createdAt).toLocaleString(isBn ? "bn-BD" : undefined)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {creditData.pagination?.totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-zinc-100 dark:border-zinc-800">
                    <PaginationBar
                      page={creditPage}
                      totalPages={creditData.pagination.totalPages}
                      total={creditData.pagination.total}
                      pageSize={creditData.pagination.limit || 20}
                      currentCount={creditData.transactions.length}
                      onPage={setCreditPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── Payment history tab ──                                         */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "paymenthistory" && user?.role === "OWNER" && (
        <div className="pt-2">
          <PaymentHistoryTable />
        </div>
      )}
    </div>
  );
}
